import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  createTheme,
  ThemeProvider,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DownloadIcon from '@mui/icons-material/Download';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PanoramaIcon from '@mui/icons-material/Panorama';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AddIcon from '@mui/icons-material/Add';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import TerminalIcon from '@mui/icons-material/Terminal';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ReplayIcon from '@mui/icons-material/Replay';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShareIcon from '@mui/icons-material/Share';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import { 
  fetchGameVideoDetails, 
  downloadCustomClip, 
  downloadHighlightClip, 
  getHighlightClipUrl, 
  addGameEvent, 
  deleteGameEvent, 
  retryGameDownload, 
  fetchVideoStreamUrl, 
  fetchHighlightClipStreamUrl, 
  refreshAuthToken, 
  onTokenRefreshed, 
  getAuthToken, 
  updateGamePeriods,
  updateGameGoal,
  fetchAudioWaveform,
  fetchStoryboardMetadata,
  fetchTimelineEvents,
  createTimelineEvent,
  updateTimelineEvent,
  deleteTimelineEvent,
  updateTimelineEventShares,
} from '../../utils/apiClient';
import { GameVideoSummary, GoalHighlight, GamePeriod, AudioWaveformSummary, GoalEventData, StoryboardMetadata, TimelineEventItem, TimelineNote, TimelineClip, GeneralAccessLevel, ShareAccessRole, TimelineShareTarget } from '../../types';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { getBackendUrl } from '../../config';
import { useRoster } from '../../RosterContext';
import { isJetsHomeGame, getGoalTeamName, getTargetTeamName } from '../../utils/gameScoreUtils';
import { TaskDebugLogsModal } from './TaskDebugLogsModal';
import { InspectorSidebar } from './studio/InspectorSidebar';
import { TransportControlsBar } from './studio/TransportControlsBar';
import { FullWidthTimeline } from './studio/FullWidthTimeline';
import { StoryboardPreview } from './studio/StoryboardPreview';
import { MobileWatchView } from './MobileWatchView';
import { TimelineShareModal } from './studio/TimelineShareModal';
import { DownloadClipFeedDialog } from './studio/DownloadClipFeedDialog';

const studioTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#141416',
      paper: '#1c1c1f',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
    },
  },
  typography: {
    fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

export const parseTimeInput = (val: string | number): number => {
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const clean = (val || '').trim();
  if (clean.includes(':')) {
    const parts = clean.split(':').map((p) => parseFloat(p) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    return parts[0] * 60 + parts[1];
  }
  return parseFloat(clean) || 0;
};

export const deriveDefaultPeriods = (game: GameVideoSummary | null, totalDurSec: number): GamePeriod[] => {
  if (game?.periods && game.periods.length > 0) {
    return game.periods;
  }
  const dur = totalDurSec > 0 ? totalDurSec : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 3600);
  const baseStart = game?.timeline?.puckDrop || game?.timeline?.actualGameStart;
  const baseTime = baseStart ? new Date(`1970-01-01T${baseStart}Z`).getTime() : null;

  const calcTime = (timeStr?: string, fallbackFraction?: number) => {
    if (timeStr && baseTime) {
      const t = new Date(`1970-01-01T${timeStr}Z`).getTime();
      return Math.max(0, (t - baseTime) / 1000);
    }
    return fallbackFraction !== undefined ? dur * fallbackFraction : 0;
  };

  const p2Sec = calcTime(game?.timeline?.period2Start, 0.33);
  const p3Sec = calcTime(game?.timeline?.period3Start, 0.66);
  const otSec = game?.timeline?.overtimeStart ? calcTime(game.timeline.overtimeStart) : 0;

  const p1End = p2Sec > 0 ? Math.min(p2Sec, dur * 0.33) : dur * 0.33;
  const p2End = p3Sec > p2Sec ? Math.min(p3Sec, p2Sec + (dur * 0.33)) : dur * 0.66;
  const p3End = otSec > p3Sec ? otSec : dur;

  const res: GamePeriod[] = [
    { period: 1, label: 'Period 1', startTime: 0, endTime: Math.round(p1End * 10) / 10 },
    { period: 2, label: 'Period 2', startTime: Math.round(p2Sec * 10) / 10, endTime: Math.round(p2End * 10) / 10 },
    { period: 3, label: 'Period 3', startTime: Math.round(p3Sec * 10) / 10, endTime: Math.round(p3End * 10) / 10 },
  ];

  if (otSec > 0) {
    res.push({
      period: 4,
      label: 'Overtime',
      startTime: Math.round(otSec * 10) / 10,
      endTime: Math.round(dur * 10) / 10,
    });
  }

  return res;
};

const GameWatchPage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthReady, userProfile, isAdmin, isTeamManager, rosters = {} } = useRoster();
  const userEmail = (userProfile?.email || auth.currentUser?.email || '').trim().toLowerCase();
  const canAccess = Boolean(userEmail || isAuthReady);
  const canManageEvents = Boolean(isAdmin || isTeamManager || userProfile?.isAdmin || userProfile?.isTeamManager);
  const canEditTimeline = Boolean(isAdmin || userProfile?.isAdmin);

  const [game, setGame] = useState<GameVideoSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const isJetsHome = useMemo(() => isJetsHomeGame(game), [game]);
  const focalTeamShort = useMemo(() => getTargetTeamName(game, true), [game]);
  const focalTeamFull = useMemo(() => getTargetTeamName(game, false), [game]);

  // Responsive Mobile Detection & Desktop Override
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscapePhone = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobileBreakpoint = isDownMd || isLandscapePhone;
  const [forceDesktopMode, setForceDesktopMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem('jets_video_studio_force_desktop') === 'true';
    } catch {
      return false;
    }
  });
  const isMobile = isMobileBreakpoint && !forceDesktopMode;

  const handleToggleDesktopMode = () => {
    const nextVal = !forceDesktopMode;
    setForceDesktopMode(nextVal);
    try {
      if (nextVal) {
        localStorage.setItem('jets_video_studio_force_desktop', 'true');
      } else {
        localStorage.removeItem('jets_video_studio_force_desktop');
      }
    } catch {}
  };

  // Video Player State
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);
  const minimapCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const minimapBoxRef = useRef<HTMLDivElement | null>(null);

  const [selectedFeed, setSelectedFeed] = useState<'auto' | 'pano'>('auto');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [feedNotice, setFeedNotice] = useState<string | null>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFs = Boolean(document.fullscreenElement);
      setIsFullscreen(isFs);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && !isFullscreen) {
        if (videoWorkspaceRef.current?.requestFullscreen) {
          await videoWorkspaceRef.current.requestFullscreen();
        } else if ((videoWorkspaceRef.current as any)?.webkitRequestFullscreen) {
          await (videoWorkspaceRef.current as any).webkitRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch {
      setIsFullscreen((prev) => !prev);
    }
  };
  const [logsModalOpen, setLogsModalOpen] = useState<boolean>(false);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [workerNotification, setWorkerNotification] = useState<{ message: string; severity: 'info' | 'success' | 'warning' | 'error' } | null>(null);

  const pendingSeekTimeRef = useRef<number | null>(null);
  const wasPlayingBeforeFeedChange = useRef<boolean>(false);

  // Manual Event Marker Dialog State
  const [eventDialogOpen, setEventDialogOpen] = useState<boolean>(false);
  const [eventFormType, setEventFormType] = useState<string>('goal');
  const [eventFormTeam, setEventFormTeam] = useState<string>('Jets');
  const [eventFormScore, setEventFormScore] = useState<string>('');
  const [eventFormPeriod, setEventFormPeriod] = useState<number>(1);
  const [eventFormPeriodLabel, setEventFormPeriodLabel] = useState<string>('');
  const [eventFormPeriodStart, setEventFormPeriodStart] = useState<string>('');
  const [eventFormPeriodEnd, setEventFormPeriodEnd] = useState<string>('');
  const [eventFormClock, setEventFormClock] = useState<string>('');
  const [eventFormTimestamp, setEventFormTimestamp] = useState<number>(0);
  const [eventFormDescription, setEventFormDescription] = useState<string>('');
  const [savingEvent, setSavingEvent] = useState<boolean>(false);
  const [eventError, setEventError] = useState<string | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);

  // Video Zoom & Pan State
  const [zoom, setZoom] = useState<number>(1.0);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [isMinimapDragging, setIsMinimapDragging] = useState<boolean>(false);

  const panDragStart = useRef<{ clientX: number; clientY: number; panX: number; panY: number; moved: boolean }>({
    clientX: 0,
    clientY: 0,
    panX: 0,
    panY: 0,
    moved: false,
  });

  // Multi-Pane Studio Layout State
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('jets_studio_sidebar_width');
      return saved ? Math.max(260, Math.min(600, parseInt(saved, 10))) : 380;
    } catch {
      return 380;
    }
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('jets_studio_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [timelineHeight, setTimelineHeight] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('jets_studio_timeline_height');
      return saved ? Math.max(160, Math.min(380, parseInt(saved, 10))) : 240;
    } catch {
      return 240;
    }
  });
  const [activeSplitter, setActiveSplitter] = useState<'vertical' | 'horizontal' | null>(null);
  const [copiedClipUrl, setCopiedClipUrl] = useState<string | null>(null);
  const [autoScrollTimeline, setAutoScrollTimeline] = useState<boolean>(true);

  useEffect(() => {
    if (!activeSplitter) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (activeSplitter === 'vertical') {
        const newWidth = Math.max(260, Math.min(window.innerWidth * 0.5, window.innerWidth - e.clientX));
        setSidebarWidth(Math.round(newWidth));
        try {
          localStorage.setItem('jets_studio_sidebar_width', String(Math.round(newWidth)));
        } catch {}
      } else if (activeSplitter === 'horizontal') {
        const newHeight = Math.max(140, Math.min(window.innerHeight * 0.45, window.innerHeight - e.clientY));
        setTimelineHeight(Math.round(newHeight));
        try {
          localStorage.setItem('jets_studio_timeline_height', String(Math.round(newHeight)));
        } catch {}
      }
    };

    const handlePointerUp = () => {
      setActiveSplitter(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [activeSplitter]);

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('jets_studio_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  // User Auth Token & Stream URL State
  const [idToken, setIdToken] = useState<string>('');
  const [activeStreamUrl, setActiveStreamUrl] = useState<string>('');
  const [activeScrubStreamUrl, setActiveScrubStreamUrl] = useState<string>('');
  const [isScrubbingUI, setIsScrubbingUI] = useState<boolean>(false);
  const scrubVideoRef = useRef<HTMLVideoElement | null>(null);
  const isRecoveringRef = useRef<boolean>(false);
  const recoveryAttemptsRef = useRef<number>(0);
  const lastSavedPosRef = useRef<number>(0);
  const lastSyncedUrlTimeRef = useRef<number>(0);
  const scrubProxyHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Storyboard Metadata State (Phase 3 Timeline / Scrubbing Preview)
  const [storyboardMetadata, setStoryboardMetadata] = useState<StoryboardMetadata | null>(null);

  // Delayed Seeking/Loading Indicator State (Shows if seeking/decoding takes >750ms)
  const [isDelayedSeeking, setIsDelayedSeeking] = useState<boolean>(false);
  const [delayedSeekTime, setDelayedSeekTime] = useState<number>(0);
  const seekIndicatorTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startDelayedSeekIndicator = useCallback((targetTime: number) => {
    if (isActivelyScrubbingRef.current) {
      return;
    }
    setDelayedSeekTime(targetTime);
    if (seekIndicatorTimerRef.current) {
      clearTimeout(seekIndicatorTimerRef.current);
    }
    seekIndicatorTimerRef.current = setTimeout(() => {
      if (!isActivelyScrubbingRef.current) {
        setIsDelayedSeeking(true);
      }
    }, 750);
  }, []);

  const clearDelayedSeekIndicator = useCallback(() => {
    if (seekIndicatorTimerRef.current) {
      clearTimeout(seekIndicatorTimerRef.current);
      seekIndicatorTimerRef.current = null;
    }
    setIsDelayedSeeking(false);
  }, []);

  // Scaled-up Blurred Storyboard Overlay State (Main Video Viewport Feedback)
  const [isStoryboardOverlayActive, setIsStoryboardOverlayActive] = useState<boolean>(false);
  const [storyboardOverlayTime, setStoryboardOverlayTime] = useState<number>(0);
  const storyboardSafetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [playerDimensions, setPlayerDimensions] = useState<{ width: number; height: number }>({ width: 1280, height: 720 });
  const [videoElementRect, setVideoElementRect] = useState<{ width: number; height: number } | null>(null);

  // Curated Highlight Reel Mode
  const [isReelMode, setIsReelMode] = useState<boolean>(false);
  const [currentReelIndex, setCurrentReelIndex] = useState<number>(0);

  const clearStoryboardOverlay = useCallback(() => {
    setIsStoryboardOverlayActive(false);
    if (storyboardSafetyTimeoutRef.current) {
      clearTimeout(storyboardSafetyTimeoutRef.current);
      storyboardSafetyTimeoutRef.current = null;
    }
  }, []);

  const triggerStoryboardOverlay = useCallback((time: number) => {
    setStoryboardOverlayTime(time);
    setIsStoryboardOverlayActive(true);
    if (storyboardSafetyTimeoutRef.current) {
      clearTimeout(storyboardSafetyTimeoutRef.current);
    }
    // Safety auto-dismiss after 3.5s if seeked event doesn't fire
    storyboardSafetyTimeoutRef.current = setTimeout(() => {
      setIsStoryboardOverlayActive(false);
    }, 3500);
  }, []);

  // Track playerWrapperRef dimensions for pixel-perfect 1:1 overlay alignment
  useEffect(() => {
    if (loading) return;
    const el = playerWrapperRef.current;
    if (!el) return;
    if (el.clientWidth > 0 && el.clientHeight > 0) {
      setPlayerDimensions({ width: el.clientWidth, height: el.clientHeight });
    }
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setPlayerDimensions({
            width: Math.floor(entry.contentRect.width),
            height: Math.floor(entry.contentRect.height),
          });
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  // Directly track the <video> element's exact rendered layout box for 1:1 overlay alignment
  useEffect(() => {
    if (loading) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const update = () => {
      if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        setVideoAspectRatio(videoEl.videoWidth / videoEl.videoHeight);
      }
      if (videoEl.clientWidth > 0 && videoEl.clientHeight > 0) {
        setVideoElementRect({ width: videoEl.clientWidth, height: videoEl.clientHeight });
      }
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => update());
    ro.observe(videoEl);
    return () => ro.disconnect();
  }, [loading, selectedFeed, isReelMode, currentReelIndex]);

  // Compute resolved video aspect ratio
  const effectiveAspectRatio = useMemo(() => {
    return (
      videoAspectRatio
      || (videoRef.current?.videoWidth && videoRef.current?.videoHeight
        ? videoRef.current.videoWidth / videoRef.current.videoHeight
        : null)
      || (storyboardMetadata?.tileWidth && storyboardMetadata?.tileHeight
        ? storyboardMetadata.tileWidth / storyboardMetadata.tileHeight
        : null)
      || (selectedFeed === 'pano' ? (3840 / 1536) : (16 / 9))
    );
  }, [videoAspectRatio, selectedFeed, storyboardMetadata]);

  // Compute exact contained video dimensions so the blurred storyboard overlay matches <video> letterbox
  const containedOverlayDimensions = useMemo(() => {
    const containerW = playerDimensions.width || 1280;
    const containerH = playerDimensions.height || 720;
    const aspect = effectiveAspectRatio;

    const containerAspect = containerW / containerH;
    let targetW = containerW;
    let targetH = containerH;
    if (containerAspect > aspect) {
      targetH = containerH;
      targetW = Math.round(containerH * aspect);
    } else {
      targetW = containerW;
      targetH = Math.round(containerW / aspect);
    }
    return { width: Math.max(160, targetW), height: Math.max(90, targetH) };
  }, [playerDimensions, effectiveAspectRatio]);

  const overlayBoxDimensions = useMemo(() => {
    if (videoElementRect && videoElementRect.width > 50 && videoElementRect.height > 50) {
      return videoElementRect;
    }
    return containedOverlayDimensions;
  }, [videoElementRect, containedOverlayDimensions]);

  useEffect(() => {
    setVideoAspectRatio(null);
    setVideoElementRect(null);
  }, [selectedFeed]);

  const suppressStoryboardOverlayRef = useRef<boolean>(false);

  const handleVideoSeeking = useCallback(() => {
    if (videoRef.current) {
      if (suppressStoryboardOverlayRef.current) {
        suppressStoryboardOverlayRef.current = false;
      } else {
        triggerStoryboardOverlay(videoRef.current.currentTime);
      }
      if (!isActivelyScrubbingRef.current) {
        startDelayedSeekIndicator(videoRef.current.currentTime);
      }
    }
  }, [startDelayedSeekIndicator, triggerStoryboardOverlay]);

  useEffect(() => {
    const updateToken = async () => {
      if (auth.currentUser) {
        try {
          const tok = await auth.currentUser.getIdToken();
          setIdToken(tok);
        } catch (e) {
          console.error('Failed to get user ID token', e);
        }
      }
    };
    updateToken();

    // Listen to token changes and automatic background renewals via Firebase onIdTokenChanged
    const unsubAuth = onIdTokenChanged(auth, (u) => {
      if (u) {
        u.getIdToken().then(setIdToken).catch(() => {});
      } else {
        setIdToken('');
      }
    });

    // Listen to token refresh events broadcasted across the app by apiClient
    const unsubRefreshed = typeof onTokenRefreshed === 'function' ? onTokenRefreshed((newToken) => {
      setIdToken(newToken);
    }) : null;

    // Proactively refresh auth token every 30 minutes during long game viewing sessions
    const refreshInterval = setInterval(async () => {
      try {
        if (typeof refreshAuthToken === 'function') {
          const fresh = await refreshAuthToken();
          if (fresh) {
            setIdToken(fresh);
          }
        }
      } catch (err) {
        console.warn('Proactive token refresh warning:', err);
      }
    }, 30 * 60 * 1000);

    return () => {
      if (typeof unsubAuth === 'function') unsubAuth();
      if (typeof unsubRefreshed === 'function') unsubRefreshed();
      clearInterval(refreshInterval);
    };
  }, []);

  // iMovie-Style Timeline Viewport & Zoom State
  const [timelineZoom, setTimelineZoom] = useState<number>(1.0);
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);
  const [timelineBaseWidth, setTimelineBaseWidth] = useState<number>(() => {
    if (typeof window !== 'undefined' && window.innerWidth > 0) {
      return Math.max(800, window.innerWidth - 32);
    }
    return 1000;
  });

  // Measure container base width with ResizeObserver
  useEffect(() => {
    if (loading || !isAuthReady || !game) return;
    const scrollEl = timelineScrollRef.current;
    if (!scrollEl) return;
    if (scrollEl.clientWidth > 0) {
      setTimelineBaseWidth(scrollEl.clientWidth);
    }
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setTimelineBaseWidth(Math.floor(entry.contentRect.width));
        }
      }
    });
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [loading, isAuthReady, game]);

  const trackWidth = Math.max(timelineBaseWidth, Math.round(timelineBaseWidth * timelineZoom));

  // In / Out Clip Trimming State
  const [clipIn, setClipIn] = useState<number | null>(null);
  const [clipOut, setClipOut] = useState<number | null>(null);
  const [clipMark, setClipMark] = useState<number | null>(null);
  const [downloadingRangeClip, setDownloadingRangeClip] = useState<boolean>(false);

  // Multi-Track Studio State
  const [periods, setPeriods] = useState<GamePeriod[]>([]);
  const [waveform, setWaveform] = useState<AudioWaveformSummary | null>(null);
  const [isLoadingWaveform, setIsLoadingWaveform] = useState<boolean>(false);
  const [isVideoBuffering, setIsVideoBuffering] = useState<boolean>(false);
  const [selectedTimelineItem, setSelectedTimelineItem] = useState<
    | { type: 'period'; periodNumber: number }
    | { type: 'goal'; goalNumber: number; eventId?: string }
    | { type: 'note'; id: string }
    | { type: 'clip'; id: string }
    | null
  >(null);
  // Active Unlocked Draft Sessions: Holds items currently unlocked and their original snapshots
  const [unlockedTimelineItems, setUnlockedTimelineItems] = useState<
    Array<{
      type: 'period' | 'goal' | 'clip' | string;
      id: number | string;
      initialSnapshot: any;
    }>
  >([]);

  // Computed active unlocked item for single-item references
  const unlockedTimelineItem = unlockedTimelineItems.length > 0 ? unlockedTimelineItems[unlockedTimelineItems.length - 1] : null;

  // Notes & Clips Timeline Events State
  const [timelineEvents, setTimelineEvents] = useState<TimelineEventItem[]>([]);
  const timelineEventsRef = useRef<TimelineEventItem[]>([]);
  timelineEventsRef.current = timelineEvents;

  // Timeline Event Modals State
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [shareTargetEvent, setShareTargetEvent] = useState<TimelineEventItem | null>(null);
  const [downloadFeedDialogOpen, setDownloadFeedDialogOpen] = useState<boolean>(false);
  const [downloadTargetClip, setDownloadTargetClip] = useState<TimelineClip | null>(null);

  const matchesGoalId = (g: GoalEventData, idOrNum: number | string): boolean => {
    if (typeof idOrNum === 'string' && isNaN(Number(idOrNum))) {
      return g.eventId === idOrNum || g.id === idOrNum || `goal-${g.goalNumber}` === idOrNum;
    }
    const asNum = Number(idOrNum);
    return (
      (g.eventId !== undefined && String(g.eventId) === String(idOrNum)) ||
      (g.id !== undefined && String(g.id) === String(idOrNum)) ||
      (!isNaN(asNum) && g.goalNumber === asNum)
    );
  };

  const getGoalIdentifier = (g: GoalEventData): string | number => g.eventId || g.id || g.goalNumber;

  const sequenceGoalsChronologically = (goalsList: GoalEventData[]): GoalEventData[] => {
    const sorted = [...goalsList].sort((a, b) => {
      const aTs = Number(a.videoTimestamp ?? a.clipStartSeconds ?? 0);
      const bTs = Number(b.videoTimestamp ?? b.clipStartSeconds ?? 0);
      if (aTs !== bTs) return aTs - bTs;
      const aStart = Number(a.clipStartSeconds ?? aTs);
      const bStart = Number(b.clipStartSeconds ?? bTs);
      if (aStart !== bStart) return aStart - bStart;
      return String(a.eventId || a.id || '').localeCompare(String(b.eventId || b.id || ''));
    });
    return sorted.map((g, idx) => ({
      ...g,
      goalNumber: idx + 1,
    }));
  };

  const handleUnlockItem = (type: 'period' | 'goal' | 'clip' | string, id: number | string) => {
    let snapshot: any = null;
    if (type === 'period') {
      const p = periodsRef.current.find((item) => item.period === id);
      snapshot = p ? JSON.parse(JSON.stringify(p)) : null;
    } else if (type === 'goal') {
      const g = gameRef.current?.goals?.find((item: any) => matchesGoalId(item, id));
      snapshot = g ? JSON.parse(JSON.stringify(g)) : null;
    } else if (type === 'clip') {
      const c = timelineEventsRef.current.find((item) => item.id === id);
      snapshot = c ? JSON.parse(JSON.stringify(c)) : null;
    }
    setUnlockedTimelineItems((prev) => {
      if (prev.some((item) => item.type === type && String(item.id) === String(id))) {
        return prev;
      }
      return [...prev, { type, id, initialSnapshot: snapshot }];
    });
  };

  const handleLockItem = (type: 'period' | 'goal' | 'clip' | string, id: number | string) => {
    const matchedGoal = type === 'goal' ? gameRef.current?.goals?.find((g) => matchesGoalId(g, id)) : undefined;
    setUnlockedTimelineItems((prev) =>
      prev.filter((item) => {
        if (item.type !== type) return true;
        if (String(item.id) === String(id)) return false;
        if (type === 'goal' && matchedGoal && matchesGoalId(matchedGoal, item.id)) return false;
        return true;
      })
    );
  };

  const handleDiscardDraft = (type?: string, id?: number | string) => {
    if (type && id !== undefined) {
      const target = unlockedTimelineItems.find(
        (item) => item.type === type && String(item.id) === String(id)
      );
      if (target?.initialSnapshot) {
        if (type === 'period') {
          setPeriods((prev) => prev.map((p) => (p.period === id ? target.initialSnapshot : p)));
        } else if (type === 'goal') {
          setGame((prev) => {
            if (!prev) return null;
            const restored = (prev.goals || []).map((g) => (matchesGoalId(g, id) ? target.initialSnapshot : g));
            return {
              ...prev,
              goals: sequenceGoalsChronologically(restored),
            };
          });
        } else if (type === 'clip') {
          setTimelineEvents((prev) => prev.map((ev) => (ev.id === id ? target.initialSnapshot : ev)));
        }
      }
      setUnlockedTimelineItems((prev) =>
        prev.filter((item) => !(item.type === type && String(item.id) === String(id)))
      );
    } else {
      // Discard all open unlocked drafts
      unlockedTimelineItems.forEach(({ type: t, id: i, initialSnapshot }) => {
        if (initialSnapshot) {
          if (t === 'period') {
            setPeriods((prev) => prev.map((p) => (p.period === i ? initialSnapshot : p)));
          } else if (t === 'goal') {
            setGame((prev) => {
              if (!prev) return null;
              const restored = (prev.goals || []).map((g) => (matchesGoalId(g, i) ? initialSnapshot : g));
              return {
                ...prev,
                goals: sequenceGoalsChronologically(restored),
              };
            });
          } else if (t === 'clip') {
            setTimelineEvents((prev) => prev.map((ev) => (ev.id === i ? initialSnapshot : ev)));
          }
        }
      });
      setUnlockedTimelineItems([]);
    }
  };

  const handleSelectPeriod = (periodNumber: number) => {
    setSelectedTimelineItem({ type: 'period', periodNumber });
  };

  const handleSelectGoal = (goalIdOrNumber: number | string) => {
    const matched = gameRef.current?.goals?.find((g) => matchesGoalId(g, goalIdOrNumber));
    const goalNum = matched ? matched.goalNumber : (typeof goalIdOrNumber === 'number' ? goalIdOrNumber : 1);
    const evtId = matched ? (matched.eventId || matched.id) : (typeof goalIdOrNumber === 'string' ? goalIdOrNumber : undefined);
    setSelectedTimelineItem({
      type: 'goal',
      goalNumber: goalNum,
      ...(evtId ? { eventId: evtId } : {}),
    });
  };

  const handleSelectNote = (id: string) => {
    setSelectedTimelineItem({ type: 'note', id });
  };

  const handleSelectClip = (id: string) => {
    setSelectedTimelineItem({ type: 'clip', id });
  };

  const handleDeselectItem = () => {
    setSelectedTimelineItem(null);
  };
  const [undoStack, setUndoStack] = useState<
    Array<{ type: 'period' | 'goal' | 'add_goal' | 'delete_goal' | 'convert'; description: string; prev: any; next: any }>
  >([]);
  const [redoStack, setRedoStack] = useState<
    Array<{ type: 'period' | 'goal' | 'add_goal' | 'delete_goal' | 'convert'; description: string; prev: any; next: any }>
  >([]);

  // Active drag state: 'playhead' | 'start' | 'end' | 'window' | 'period-start' | 'period-end' | 'goal' | 'clip-start' | 'clip-end' | 'clip-window' | 'note' | null
  const [activeTimelineDrag, setActiveTimelineDrag] = useState<
    'playhead' | 'start' | 'end' | 'window' | 'period-start' | 'period-end' | 'goal' | 'goal-start' | 'goal-end' | 'goal-window' | 'clip-start' | 'clip-end' | 'clip-window' | 'note' | null
  >(null);
  const dragStartX = useRef<number>(0);
  const dragInitialIn = useRef<number>(0);
  const dragInitialOut = useRef<number>(0);
  const rangeClipStartRef = useRef<number>(0);
  const rangeClipEndRef = useRef<number>(0);

  // Multi-Track Dragging Refs
  const dragPeriodNum = useRef<number | null>(null);
  const dragPeriodHandle = useRef<'start' | 'end' | null>(null);
  const dragGoalNum = useRef<number | string | null>(null);
  const dragGoalHandle = useRef<'start' | 'end' | 'window' | null>(null);
  const dragInitialGoal = useRef<{
    goalId: number | string;
    goalNumber: number;
    videoTimestamp: number;
    clipStartSeconds: number;
    clipEndSeconds: number;
    durationSeconds: number;
  } | null>(null);
  const dragClipId = useRef<string | null>(null);
  const dragClipHandle = useRef<'start' | 'end' | 'window' | null>(null);
  const dragInitialClip = useRef<TimelineClip | null>(null);
  const dragNoteId = useRef<string | null>(null);
  const dragInitialNoteTime = useRef<number>(0);
  const dragInitialPeriods = useRef<GamePeriod[]>([]);
  const dragInitialGoalTime = useRef<number>(0);
  const periodsRef = useRef<GamePeriod[]>([]);
  periodsRef.current = periods;
  const gameRef = useRef<GameVideoSummary | null>(null);
  gameRef.current = game;

  // High-Performance Seeking & Scrubbing Engine Refs
  const isActivelyScrubbingRef = useRef<boolean>(false);
  // Always stores pending scrub time in BASELINE timeline coordinates (not physical video coordinates)
  const pendingScrubTimeRef = useRef<number | null>(null);
  const isExecutingSeekRef = useRef<boolean>(false);
  const rafSeekIdRef = useRef<number | null>(null);
  const lastDispatchedSeekTimeRef = useRef<number | null>(null);
  const lastTargetTimelineTimeRef = useRef<number | null>(null);
  const scrubSafetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasPlayingBeforeScrubRef = useRef<boolean>(false);
  const isPreviewingClipRegionRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      if (rafSeekIdRef.current !== null) {
        cancelAnimationFrame(rafSeekIdRef.current);
      }
      if (scrubSafetyTimeoutRef.current) {
        clearTimeout(scrubSafetyTimeoutRef.current);
      }
      if (scrubProxyHideTimeoutRef.current) {
        clearTimeout(scrubProxyHideTimeoutRef.current);
      }
      if (seekIndicatorTimerRef.current) {
        clearTimeout(seekIndicatorTimerRef.current);
      }
    };
  }, []);


  // Custom Segment Clipper Dialog State
  const [clipperOpen, setClipperOpen] = useState<boolean>(false);
  const [clipStart, setClipStart] = useState<number>(0);
  const [clipEnd, setClipEnd] = useState<number>(30);
  const [cuttingClip, setCuttingClip] = useState<boolean>(false);
  const [clipError, setClipError] = useState<string | null>(null);
  const [downloadSnackbar, setDownloadSnackbar] = useState<{ text: string; severity: 'info' | 'success' | 'error' } | null>(null);

  // Derived Clip Selection Range (Download Region)
  const hasClipMark = clipIn !== null && clipOut !== null && clipOut > clipIn;
  const rangeClipStart = clipIn !== null && clipOut !== null ? Math.min(clipIn, clipOut) : 0;
  const rangeClipEnd = clipIn !== null && clipOut !== null ? Math.max(clipIn, clipOut) : 0;
  const rangeClipDuration = hasClipMark ? rangeClipEnd - rangeClipStart : 0;
  rangeClipStartRef.current = rangeClipStart;
  rangeClipEndRef.current = rangeClipEnd;

  const setCenteredClipRegion = useCallback((centerTime: number) => {
    const cur = Math.max(0, centerTime);
    const halfLen = 15;
    const clipLen = 30;

    const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 0);

    let start = Math.max(0, cur - halfLen);
    let end = cur + halfLen;

    if (totalDur > 0) {
      if (end > totalDur) {
        end = totalDur;
        start = Math.max(0, totalDur - clipLen);
      }
      if (start < 0) {
        start = 0;
        end = Math.min(totalDur, clipLen);
      }
    }

    start = parseFloat(start.toFixed(1));
    end = parseFloat(end.toFixed(1));

    setClipIn(start);
    setClipOut(end);
    setClipMark(cur);
    isPreviewingClipRegionRef.current = true;
  }, [duration, game]);

  const syncUrlTimestamp = useCallback((timeSec: number, feed: 'auto' | 'pano') => {
    if (!gameId || isReelMode) return;
    try {
      const url = new URL(window.location.href);
      if (timeSec > 5) {
        url.searchParams.set('t', Math.floor(timeSec).toString());
      } else {
        url.searchParams.delete('t');
      }
      if (feed && feed !== 'auto') {
        url.searchParams.set('feed', feed);
      } else {
        url.searchParams.delete('feed');
      }
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch (e) {}
  }, [gameId, isReelMode]);

  const handleShareCurrentTime = () => {
    if (!game) return;
    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
    const curSec = videoRef.current
      ? Math.floor(Math.max(0, videoRef.current.currentTime - curFeedOffset))
      : Math.floor(currentTime);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('t', curSec.toString());
      if (selectedFeed && selectedFeed !== 'auto') {
        url.searchParams.set('feed', selectedFeed);
      } else {
        url.searchParams.delete('feed');
      }
      const shareUrl = url.toString();
      navigator.clipboard.writeText(shareUrl);
      setCopiedClipUrl(shareUrl);
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch (e) {
      console.warn('Failed to copy share link:', e);
    }
  };

  useEffect(() => {
    if (!isAuthReady || !gameId || !canAccess) return;

    const loadGame = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchGameVideoDetails(gameId);
        setGame(data);

        // Populate or derive periods
        if (data.periods && data.periods.length > 0) {
          setPeriods(data.periods);
        } else {
          setPeriods(deriveDefaultPeriods(data, duration));
        }

        // Populate timeline events
        if (data.timelineEvents && data.timelineEvents.length > 0) {
          setTimelineEvents(data.timelineEvents);
        } else if (typeof fetchTimelineEvents === 'function') {
          try {
            const evPromise = fetchTimelineEvents(gameId);
            if (evPromise && typeof evPromise.then === 'function') {
              evPromise
                .then((evs) => {
                  if (evs && evs.length > 0) {
                    setTimelineEvents(evs);
                  }
                })
                .catch(() => {});
            }
          } catch (err) {
            // Ignore in tests or offline
          }
        }

        // Fetch audio waveform summary
        if (data.waveform) {
          setWaveform(data.waveform);
          setIsLoadingWaveform(false);
        } else if (typeof fetchAudioWaveform === 'function') {
          setIsLoadingWaveform(true);
          try {
            const wfPromise = fetchAudioWaveform(gameId);
            if (wfPromise && typeof wfPromise.then === 'function') {
              wfPromise
                .then((wf) => {
                  if (wf) {
                    setWaveform(wf);
                    setIsLoadingWaveform(false);
                  } else {
                    // Retry once after 4s in case on-demand backend extraction was in-flight
                    setTimeout(() => {
                      fetchAudioWaveform(gameId)
                        .then((retryWf) => {
                          if (retryWf) setWaveform(retryWf);
                        })
                        .catch(() => {})
                        .finally(() => setIsLoadingWaveform(false));
                    }, 4000);
                  }
                })
                .catch(() => {
                  setIsLoadingWaveform(false);
                });
            } else {
              setIsLoadingWaveform(false);
            }
          } catch (e) {
            setIsLoadingWaveform(false);
          }
        }

        // Feed selection from query param or default
        let targetFeed: 'auto' | 'pano' = 'auto';
        const requestedFeed = searchParams.get('feed');
        if (requestedFeed === 'pano' && data.feeds?.pano?.status === 'downloaded') {
          targetFeed = 'pano';
        } else if (requestedFeed === 'auto' && data.feeds?.auto?.status === 'downloaded') {
          targetFeed = 'auto';
        } else if (data.feeds?.auto?.status === 'downloaded') {
          targetFeed = 'auto';
        } else if (data.feeds?.pano?.status === 'downloaded') {
          targetFeed = 'pano';
        }
        setSelectedFeed(targetFeed);

        let hasDeepLink = false;
        const targetOffset = (data.feedSync?.offsets?.[targetFeed]) ?? 0;

        // Deep linking: timestamp (?t=123 or ?t=02:30 or ?t=01:15:30)
        const tParam = searchParams.get('t');
        if (tParam) {
          hasDeepLink = true;
          let seekSeconds = 0;
          if (tParam.includes(':')) {
            const parts = tParam.split(':').map(Number);
            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
              seekSeconds = parts[0] * 60 + parts[1];
            } else if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
              seekSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
            }
          } else {
            seekSeconds = parseFloat(tParam) || 0;
          }
          if (seekSeconds > 0) {
            pendingSeekTimeRef.current = Math.max(0, seekSeconds + targetOffset);
          }
        }

        // Deep linking: goal marker (?goal=2)
        const goalParam = searchParams.get('goal');
        if (goalParam && data.goals && data.goals.length > 0) {
          hasDeepLink = true;
          const gNum = parseInt(goalParam, 10);
          const matchedGoal = data.goals.find((g: any) => g.goalNumber === gNum);
          if (matchedGoal && typeof matchedGoal.videoTimestamp === 'number') {
            pendingSeekTimeRef.current = Math.max(0, matchedGoal.videoTimestamp + targetOffset);
          }
        }

        // Deep linking: event marker (?event=<id>)
        const eventParam = searchParams.get('event');
        if (eventParam && data.timelineEvents && data.timelineEvents.length > 0) {
          hasDeepLink = true;
          const matchedEvent = data.timelineEvents.find((ev: any) => ev.id === eventParam);
          if (matchedEvent) {
            const seekTime = matchedEvent.eventType === 'note' ? matchedEvent.time : matchedEvent.startTime;
            pendingSeekTimeRef.current = Math.max(0, seekTime + targetOffset);
            if (matchedEvent.eventType === 'note') {
              setSelectedTimelineItem({ type: 'note', id: matchedEvent.id });
            } else {
              setSelectedTimelineItem({ type: 'clip', id: matchedEvent.id });
            }
          }
        }

        // Restore saved playback position if no explicit deep link was specified
        if (!hasDeepLink) {
          try {
            const savedRaw = localStorage.getItem(`jets_playback_pos_${gameId}`);
            if (savedRaw) {
              const saved = JSON.parse(savedRaw);
              if (saved && typeof saved.time === 'number' && saved.time > 10) {
                if (saved.feed && (saved.feed === 'auto' || saved.feed === 'pano')) {
                  const feedKey = saved.feed as 'auto' | 'pano';
                  if (data.feeds?.[feedKey]?.status === 'downloaded') {
                    targetFeed = feedKey;
                    setSelectedFeed(feedKey);
                  }
                }
                const finalOffset = (data.feedSync?.offsets?.[targetFeed]) ?? 0;
                pendingSeekTimeRef.current = Math.max(0, saved.time + finalOffset);
              }
            }
          } catch (e) {}
        }

        // Resolve direct GCS streaming URL (24h valid, completely independent of token expiry)
        try {
          const streamInfo = await fetchVideoStreamUrl(gameId, targetFeed).catch(() => null);
          const curToken = await getAuthToken();
          if (streamInfo?.direct) {
            setActiveStreamUrl(streamInfo.url);
          } else if (streamInfo?.url) {
            setActiveStreamUrl(`${getBackendUrl()}${streamInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
          } else {
            setActiveStreamUrl(`${getBackendUrl()}/api/videos/${gameId}/stream?feed=${targetFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
          }
        } catch (streamErr) {
          const curToken = await getAuthToken();
          setActiveStreamUrl(`${getBackendUrl()}/api/videos/${gameId}/stream?feed=${targetFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
        }

        // Resolve 360p ultra-dense GOP scrub proxy URL for instant seeking
        try {
          const scrubInfo = await fetchVideoStreamUrl(gameId, targetFeed, 'scrub').catch(() => null);
          const curToken = await getAuthToken();
          if (scrubInfo?.direct) {
            setActiveScrubStreamUrl(scrubInfo.url);
          } else if (scrubInfo?.url) {
            setActiveScrubStreamUrl(`${getBackendUrl()}${scrubInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
          } else {
            setActiveScrubStreamUrl(`${getBackendUrl()}/api/videos/${gameId}/stream?feed=${targetFeed}&variant=scrub${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
          }
        } catch {
          const curToken = await getAuthToken();
          setActiveScrubStreamUrl(`${getBackendUrl()}/api/videos/${gameId}/stream?feed=${targetFeed}&variant=scrub${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
        }

        // Deep linking: open debug logs modal (?debug=true or ?logs=true)
        if (searchParams.get('debug') === 'true' || searchParams.get('logs') === 'true') {
          setLogsModalOpen(true);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load game video details.');
      } finally {
        setLoading(false);
      }
    };

    loadGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, canAccess, isAuthReady, searchParams]);

  // Sync periods whenever game periods update or become available
  useEffect(() => {
    if (game?.periods && game.periods.length > 0) {
      setPeriods((prev) => {
        if (unlockedTimelineItems.length === 0) {
          return game.periods!;
        }
        const unlockedPeriodIds = new Set(
          unlockedTimelineItems.filter((item) => item.type === 'period').map((item) => Number(item.id))
        );
        return game.periods!.map((p) => {
          if (unlockedPeriodIds.has(p.period)) {
            const current = prev.find((prevP) => prevP.period === p.period);
            return current || p;
          }
          return p;
        });
      });
    } else if (game && periods.length === 0) {
      setPeriods(deriveDefaultPeriods(game, duration));
    }
  }, [game, duration, periods.length, unlockedTimelineItems]);

  // Sync timeline events whenever game updates (including non-goal manualEvents)
  useEffect(() => {
    const rawEvents = (game?.timelineEvents || []).map((ev: any) => ({
      ...ev,
      eventType: ev.eventType || ev.type || 'clip',
    }));
    const manualNonGoals = (game?.manualEvents || []).filter((me) => !me.type?.toLowerCase().includes('goal'));
    const existingIds = new Set(rawEvents.map((e) => e.id));
    const merged = [...rawEvents];
    for (const me of manualNonGoals) {
      if (!existingIds.has(me.id)) {
        const ts = me.videoTimestamp || 0;
        const clip: TimelineClip = {
          id: me.id,
          eventType: 'clip',
          title: me.description || `${me.type.charAt(0).toUpperCase() + me.type.slice(1)}: ${me.scoringTeam || focalTeamShort}`,
          startTime: Math.max(0, ts - 15),
          endTime: ts + 15,
          duration: 30,
          notes: me.description || '',
          generalAccess: 'public',
          shares: [],
          isLocked: true,
        };
        merged.push(clip);
        existingIds.add(me.id);
      }
    }
    if (merged.length > 0 || (game?.timelineEvents && game.timelineEvents.length === 0)) {
      setTimelineEvents((prevEvents) => {
        if (unlockedTimelineItems.length === 0) {
          return merged as TimelineEventItem[];
        }
        const unlockedEventIds = new Set(
          unlockedTimelineItems.filter((item) => item.type === 'clip' || item.type === 'note').map((item) => String(item.id))
        );
        return (merged as TimelineEventItem[]).map((e) => {
          if (unlockedEventIds.has(e.id)) {
            const current = prevEvents.find((p) => p.id === e.id);
            return current || e;
          }
          return e;
        });
      });
    }
  }, [game?.timelineEvents, game?.manualEvents, unlockedTimelineItems, focalTeamShort]);

  // Deep linking: Watch for ?event=<id> param if timelineEvents change
  useEffect(() => {
    const eventParam = searchParams.get('event');
    if (!eventParam || timelineEvents.length === 0) return;
    const matched = timelineEvents.find((e) => e.id === eventParam);
    if (matched) {
      if (matched.eventType === 'note') {
        setSelectedTimelineItem({ type: 'note', id: matched.id });
      } else {
        setSelectedTimelineItem({ type: 'clip', id: matched.id });
      }
    }
  }, [searchParams, timelineEvents]);

  // Fetch storyboard metadata for scrubbing previews (Phase 3)
  useEffect(() => {
    if (!game?.gameId) {
      setStoryboardMetadata(null);
      return;
    }

    const feedMeta = game.feeds?.[selectedFeed];
    if (feedMeta?.hasStoryboard) {
      setStoryboardMetadata({
        hasStoryboard: true,
        feed: selectedFeed,
        interval: feedMeta.storyboardInterval || 2,
        sheetCount: feedMeta.storyboardSheetCount || 0,
        cols: 10,
        rows: 10,
        tilesPerSheet: 100,
        tileWidth: 160,
        tileHeight: 90,
        format: 'jpg',
      });
    } else if (typeof fetchStoryboardMetadata === 'function') {
      let isSubscribed = true;
      try {
        const promise = fetchStoryboardMetadata(game.gameId, selectedFeed);
        if (promise && typeof promise.then === 'function') {
          promise
            .then((meta) => {
              if (isSubscribed) {
                setStoryboardMetadata(meta);
              }
            })
            .catch(() => {
              if (isSubscribed) {
                setStoryboardMetadata(null);
              }
            });
        }
      } catch {
        if (isSubscribed) {
          setStoryboardMetadata(null);
        }
      }
      return () => {
        isSubscribed = false;
      };
    }
  }, [game?.gameId, selectedFeed, game?.feeds]);

  // Auto-polling for active background tasks or scheduled executions
  const isWorkerActive = game?.status === 'processing' || game?.lastTaskStatus === 'running';
  const isTaskScheduled = Boolean(
    game?.status === 'scheduled' ||
    (game?.scheduledExecutionTime &&
      game?.status !== 'complete' &&
      !isWorkerActive &&
      new Date(game.scheduledExecutionTime).getTime() > Date.now())
  );
  const shouldPoll = isWorkerActive || isTaskScheduled || isRetrying;

  const prevPanoDownloadedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (game?.feeds?.pano?.status && prevPanoDownloadedRef.current === null) {
      prevPanoDownloadedRef.current = game.feeds.pano.status === 'downloaded';
    }
  }, [gameId, game?.feeds?.pano?.status]);

  useEffect(() => {
    if (!shouldPoll || !gameId) return;

    const interval = setInterval(async () => {
      try {
        const updated = await fetchGameVideoDetails(gameId);
        setGame(updated);

        const wasDownloaded = prevPanoDownloadedRef.current === true;
        const isDownloadedNow = updated.feeds?.pano?.status === 'downloaded';

        // Only notify if pano feed newly transitioned to downloaded or manual retry finished
        if ((!wasDownloaded && isDownloadedNow) || (isRetrying && updated.status === 'complete')) {
          setWorkerNotification({
            message: 'Panoramic video feed is ready! You can now toggle to the panoramic view.',
            severity: 'success',
          });
          setIsRetrying(false);
          prevPanoDownloadedRef.current = true;
        }
      } catch (e) {
        console.warn('Worker polling check failed:', e);
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [shouldPoll, gameId, isRetrying]);

  const handleTriggerRetry = async (feedType: string = 'pano') => {
    if (!gameId) return;
    setIsRetrying(true);
    setWorkerNotification({
      message: `Dispatching ${feedType.toUpperCase()} download retry to Cloud Tasks...`,
      severity: 'info',
    });
    try {
      const res = await retryGameDownload(gameId, feedType);
      const timeStr = res?.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : 'in ~1 minute';
      setWorkerNotification({
        message: `Task ${res?.taskName || 'queued'} scheduled for execution at ${timeStr}. Monitoring worker progress...`,
        severity: 'info',
      });
      const updated = await fetchGameVideoDetails(gameId);
      setGame(updated);
    } catch (err: any) {
      setWorkerNotification({
        message: `Failed to trigger retry: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
      setIsRetrying(false);
    }
  };

  // Video Zoom Pan Helpers
  const getMaxPan = (currentZoom: number) => {
    if (!playerWrapperRef.current || currentZoom <= 1.0) {
      return { x: 0, y: 0 };
    }
    const W = playerWrapperRef.current.clientWidth || 800;
    const H = playerWrapperRef.current.clientHeight || 450;
    return {
      x: Math.max(0, (W * (currentZoom - 1)) / 2),
      y: Math.max(0, (H * (currentZoom - 1)) / 2),
    };
  };

  const handleZoomIn = () => {
    const step = zoom >= 3.0 ? 0.5 : 0.25;
    const nextZoom = Math.min(10.0, parseFloat((zoom + step).toFixed(2)));
    setZoom(nextZoom);
    const max = getMaxPan(nextZoom);
    setPan((prev) => ({
      x: Math.max(-max.x, Math.min(max.x, prev.x)),
      y: Math.max(-max.y, Math.min(max.y, prev.y)),
    }));
  };

  const handleZoomOut = () => {
    const step = zoom > 3.0 ? 0.5 : 0.25;
    const nextZoom = Math.max(1.0, parseFloat((zoom - step).toFixed(2)));
    setZoom(nextZoom);
    if (nextZoom <= 1.0) {
      setPan({ x: 0, y: 0 });
    } else {
      const max = getMaxPan(nextZoom);
      setPan((prev) => ({
        x: Math.max(-max.x, Math.min(max.x, prev.x)),
        y: Math.max(-max.y, Math.min(max.y, prev.y)),
      }));
    }
  };

  const handleZoomReset = () => {
    setZoom(1.0);
    setPan({ x: 0, y: 0 });
  };

  const handleWheelZoom = (e: React.WheelEvent<HTMLDivElement>) => {
    // Detect pinch-to-zoom (trackpad pinch sets ctrlKey) or Ctrl/Meta + wheel
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = -e.deltaY;
      const factor = delta > 0 ? 1.15 : 0.87;
      const nextZoom = Math.max(1.0, Math.min(10.0, parseFloat((zoom * factor).toFixed(2))));
      setZoom(nextZoom);
      if (nextZoom <= 1.0) {
        setPan({ x: 0, y: 0 });
      } else {
        const max = getMaxPan(nextZoom);
        setPan((prev) => ({
          x: Math.max(-max.x, Math.min(max.x, prev.x)),
          y: Math.max(-max.y, Math.min(max.y, prev.y)),
        }));
      }
    } else if (zoom > 1.05) {
      // Natural two-finger scroll on trackpad: pan the viewport smoothly
      e.preventDefault();
      const max = getMaxPan(zoom);
      setPan((prev) => ({
        x: Math.max(-max.x, Math.min(max.x, prev.x - e.deltaX)),
        y: Math.max(-max.y, Math.min(max.y, prev.y - e.deltaY)),
      }));
    }
  };

  const minimapDimensions = useMemo(() => {
    const aspect = effectiveAspectRatio || (selectedFeed === 'pano' ? (3840 / 1536) : (16 / 9));
    const width = aspect > 2.0 ? 150 : 120;
    const height = Math.max(36, Math.round(width / aspect));
    return { width, height };
  }, [effectiveAspectRatio, selectedFeed]);

  // Draw unscaled video frame on the minimap canvas
  const drawMinimapFrame = () => {
    if (!minimapCanvasRef.current || !videoRef.current || videoRef.current.readyState < 2) return;
    const ctx = minimapCanvasRef.current.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, minimapDimensions.width, minimapDimensions.height);
    }
  };

  useEffect(() => {
    if (zoom <= 1.05) return;
    drawMinimapFrame();
    if (!isPlaying) return; // No animation loop needed when paused
    let animationFrameId: number;
    const renderLoop = () => {
      if (videoRef.current && !videoRef.current.paused) {
        drawMinimapFrame();
        animationFrameId = requestAnimationFrame(renderLoop);
      }
    };
    animationFrameId = requestAnimationFrame(renderLoop);
    return () => cancelAnimationFrame(animationFrameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, isPlaying, minimapDimensions]);

  const getMinimapViewport = () => {
    const { width: mmW, height: mmH } = minimapDimensions;
    if (zoom <= 1.0) {
      return { width: mmW, height: mmH, left: 0, top: 0 };
    }
    const max = getMaxPan(zoom);
    const vpWidth = Math.max(16, mmW / zoom);
    const vpHeight = Math.max(12, mmH / zoom);

    const normX = max.x > 0 ? 0.5 - pan.x / (2 * max.x) : 0.5;
    const normY = max.y > 0 ? 0.5 - pan.y / (2 * max.y) : 0.5;

    const left = Math.max(0, Math.min(mmW - vpWidth, normX * (mmW - vpWidth)));
    const top = Math.max(0, Math.min(mmH - vpHeight, normY * (mmH - vpHeight)));

    return { width: vpWidth, height: vpHeight, left, top };
  };

  const panToMinimapCoord = (clickX: number, clickY: number) => {
    if (zoom <= 1.0) return;
    const { width: mmW, height: mmH } = minimapDimensions;
    const max = getMaxPan(zoom);
    const vpWidth = Math.max(16, mmW / zoom);
    const vpHeight = Math.max(12, mmH / zoom);

    const targetLeft = Math.max(0, Math.min(mmW - vpWidth, clickX - vpWidth / 2));
    const targetTop = Math.max(0, Math.min(mmH - vpHeight, clickY - vpHeight / 2));

    const normX = (mmW - vpWidth) > 0 ? targetLeft / (mmW - vpWidth) : 0.5;
    const normY = (mmH - vpHeight) > 0 ? targetTop / (mmH - vpHeight) : 0.5;

    const newPanX = (0.5 - normX) * (2 * max.x);
    const newPanY = (0.5 - normY) * (2 * max.y);

    setPan({
      x: Math.max(-max.x, Math.min(max.x, newPanX)),
      y: Math.max(-max.y, Math.min(max.y, newPanY)),
    });
  };

  const handleMinimapPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMinimapDragging(true);
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {}
    const rect = e.currentTarget.getBoundingClientRect();
    panToMinimapCoord(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleVideoPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // Only primary mouse button initiates drag
    if (zoom <= 1.05) return;
    if ((e.target as HTMLElement).closest('.player-overlay-control')) return;

    setIsPanning(true);
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {}
    panDragStart.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
  };

  const handleVideoPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isMinimapDragging) {
      if (e.buttons === 0) {
        setIsMinimapDragging(false);
        try {
          if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
          }
        } catch {}
        return;
      }
      if (minimapBoxRef.current) {
        const rect = minimapBoxRef.current.getBoundingClientRect();
        panToMinimapCoord(e.clientX - rect.left, e.clientY - rect.top);
      }
      return;
    }

    if (!isPanning || zoom <= 1.05) return;
    // Release pan if mouse button is not held down
    if (e.buttons === 0 || (e.buttons & 1) === 0) {
      setIsPanning(false);
      try {
        if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        }
      } catch {}
      return;
    }

    const dx = e.clientX - panDragStart.current.clientX;
    const dy = e.clientY - panDragStart.current.clientY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      panDragStart.current.moved = true;
    }
    const max = getMaxPan(zoom);
    const newX = Math.max(-max.x, Math.min(max.x, panDragStart.current.panX + dx));
    const newY = Math.max(-max.y, Math.min(max.y, panDragStart.current.panY + dy));
    setPan({ x: newX, y: newY });
  };

  const handleVideoPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      }
    } catch {}
    if (isMinimapDragging) {
      setIsMinimapDragging(false);
    }
    if (isPanning) {
      setIsPanning(false);
      if (!panDragStart.current.moved) {
        togglePlay();
      }
    }
  };

  const handleVideoPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      if ((e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      }
    } catch {}
    setIsPanning(false);
    setIsMinimapDragging(false);
  };

  // Global window listener to safeguard against stuck mouse pan when releasing outside
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      setIsPanning(false);
      setIsMinimapDragging(false);
    };
    window.addEventListener('pointerup', handleGlobalPointerUp);
    window.addEventListener('pointercancel', handleGlobalPointerUp);
    return () => {
      window.removeEventListener('pointerup', handleGlobalPointerUp);
      window.removeEventListener('pointercancel', handleGlobalPointerUp);
    };
  }, []);

  const executeSeek = useCallback(
    (time: number, isScrubbing: boolean = false) => {
      const video = videoRef.current;
      if (!video) return;

      const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 3600);
      const clampedTimelineTime = Math.max(0, Math.min(totalDur, time));
      const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
      const targetVideoTime = Math.max(0, Math.min(totalDur, time + curFeedOffset));

      lastTargetTimelineTimeRef.current = clampedTimelineTime;
      if (rangeClipEndRef.current > rangeClipStartRef.current) {
        isPreviewingClipRegionRef.current =
          clampedTimelineTime >= rangeClipStartRef.current &&
          clampedTimelineTime <= rangeClipEndRef.current;
      }

      // Always update UI state immediately for responsive slider/playhead
      setCurrentTime(clampedTimelineTime);
      if (!suppressStoryboardOverlayRef.current) {
        triggerStoryboardOverlay(targetVideoTime);
      } else {
        clearStoryboardOverlay();
      }

      if (!isScrubbing) {
        // Discrete seek or final drop on pointerup
        isActivelyScrubbingRef.current = false;
        pendingScrubTimeRef.current = null;
        if (rafSeekIdRef.current !== null) {
          cancelAnimationFrame(rafSeekIdRef.current);
          rafSeekIdRef.current = null;
        }
        if (scrubSafetyTimeoutRef.current) {
          clearTimeout(scrubSafetyTimeoutRef.current);
          scrubSafetyTimeoutRef.current = null;
        }
        isExecutingSeekRef.current = false;

        // Start delayed indicator timer if seeking/decoding takes >750ms
        startDelayedSeekIndicator(targetVideoTime);

        // Snap master video to exact target frame
        video.currentTime = targetVideoTime;
        if (Math.abs((video.currentTime || 0) - targetVideoTime) > 0.05) {
          pendingSeekTimeRef.current = targetVideoTime;
        }
        if (scrubVideoRef.current) {
          try {
            scrubVideoRef.current.currentTime = targetVideoTime;
          } catch {}
        }
        lastDispatchedSeekTimeRef.current = targetVideoTime;

        // Keep scrub proxy visible briefly until master finishes seeking, with safety fallback
        if (scrubProxyHideTimeoutRef.current) {
          clearTimeout(scrubProxyHideTimeoutRef.current);
        }
        scrubProxyHideTimeoutRef.current = setTimeout(() => {
          setIsScrubbingUI(false);
        }, 1200);
        return;
      }

      // Active scrubbing mode (pendingScrubTimeRef is stored in baseline timeline time)
      clearDelayedSeekIndicator();
      isActivelyScrubbingRef.current = true;
      setIsScrubbingUI(true);
      pendingScrubTimeRef.current = clampedTimelineTime;

      // If a seek is already in flight, wait for onSeeked to process the latest position
      if (isExecutingSeekRef.current) {
        return;
      }

      // Schedule seek on next animation frame
      if (rafSeekIdRef.current !== null) return;

      rafSeekIdRef.current = requestAnimationFrame(() => {
        rafSeekIdRef.current = null;
        const pendingTimelineTime = pendingScrubTimeRef.current;
        if (pendingTimelineTime === null || !videoRef.current) return;
        const offset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
        const targetTime = Math.max(0, Math.min(totalDur, pendingTimelineTime + offset));

        // Skip if target position is virtually identical to last dispatched
        if (
          lastDispatchedSeekTimeRef.current !== null &&
          Math.abs(lastDispatchedSeekTimeRef.current - targetTime) < 0.05
        ) {
          return;
        }

        isExecutingSeekRef.current = true;
        lastDispatchedSeekTimeRef.current = targetTime;

        // Seek proxy video if available for ultra-dense 15ms keyframes, otherwise fallback to master
        const targetElement = (scrubVideoRef.current && (scrubVideoRef.current.src || activeScrubStreamUrl)) ? scrubVideoRef.current : videoRef.current;
        if (targetElement) {
          try {
            if (typeof targetElement.fastSeek === 'function') {
              targetElement.fastSeek(targetTime);
            } else {
              targetElement.currentTime = targetTime;
            }
          } catch {
            targetElement.currentTime = targetTime;
          }
        }

        // Safety fallback timeout in case onSeeked does not fire promptly
        if (scrubSafetyTimeoutRef.current) clearTimeout(scrubSafetyTimeoutRef.current);
        scrubSafetyTimeoutRef.current = setTimeout(() => {
          if (isExecutingSeekRef.current) {
            isExecutingSeekRef.current = false;
            if (
              pendingScrubTimeRef.current !== null &&
              Math.abs(Math.max(0, Math.min(totalDur, pendingScrubTimeRef.current + offset)) - (lastDispatchedSeekTimeRef.current ?? -1)) >= 0.05
            ) {
              executeSeek(pendingScrubTimeRef.current, true);
            }
          }
        }, 50);
      });
    },
    [duration, game, selectedFeed, isReelMode, activeScrubStreamUrl, startDelayedSeekIndicator, clearDelayedSeekIndicator, triggerStoryboardOverlay, clearStoryboardOverlay]
  );

  const handleSeek = useCallback(
    (time: number, isScrubbing: boolean = false) => {
      executeSeek(time, isScrubbing);
    },
    [executeSeek]
  );

  const handleRelativeSeek = (deltaSeconds: number) => {
    if (!videoRef.current) return;
    suppressStoryboardOverlayRef.current = true;
    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
    const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 3600);
    const rawVideoTime = videoRef.current.currentTime || 0;
    const curVideoTime =
      lastDispatchedSeekTimeRef.current !== null &&
      (videoRef.current.seeking || (videoRef.current.readyState === 0 && rawVideoTime === 0 && lastDispatchedSeekTimeRef.current > 0))
        ? lastDispatchedSeekTimeRef.current
        : rawVideoTime;
    const newVideoTime = Math.max(0, Math.min(totalDur, curVideoTime + deltaSeconds));
    // Convert physical video time to baseline timeline time so executeSeek does not apply curFeedOffset twice
    const newTimelineTime = newVideoTime - curFeedOffset;
    handleSeek(newTimelineTime, false);
  };

  const handleSetPlaybackRate = (newRate: number) => {
    const clamped = Math.max(0.25, Math.min(2.5, parseFloat(newRate.toFixed(2))));
    setPlaybackRate(clamped);
    if (videoRef.current) {
      videoRef.current.playbackRate = clamped;
    }
  };

  const timeToPx = useCallback((time: number) => {
    const usableWidth = Math.max(0, trackWidth - 2 * 14);
    return 14 + (time / (duration || 1)) * usableWidth;
  }, [duration, trackWidth]);

  const getTimeFromClientX = useCallback((clientX: number) => {
    if (!timelineScrollRef.current) return 0;
    const rect = timelineScrollRef.current.getBoundingClientRect();
    const scrollLeft = timelineScrollRef.current.scrollLeft;
    const relativeX = clientX - rect.left + scrollLeft - 14;
    const usableWidth = Math.max(1, trackWidth - 2 * 14);
    return Math.max(0, Math.min(duration || 0, (relativeX / usableWidth) * (duration || 1)));
  }, [duration, trackWidth]);

  const handleTimelineZoom = (newZoom: number, anchorClientX?: number) => {
    const clamped = Math.max(1.0, Math.min(10.0, parseFloat(newZoom.toFixed(1))));
    if (timelineScrollRef.current) {
      const container = timelineScrollRef.current;
      const containerRect = container.getBoundingClientRect();
      const anchorX = anchorClientX !== undefined ? (anchorClientX - containerRect.left) : (container.clientWidth / 2);
      const anchorTime = getTimeFromClientX(anchorClientX !== undefined ? anchorClientX : containerRect.left + container.clientWidth / 2);

      const newTrackWidth = Math.max(container.clientWidth, Math.round(container.clientWidth * clamped));
      const usableWidth = Math.max(0, newTrackWidth - 2 * 14);
      const newAnchorPx = 14 + (anchorTime / (duration || 1)) * usableWidth;
      container.scrollLeft = Math.max(0, newAnchorPx - anchorX);
    }
    setTimelineZoom(clamped);
  };

  const handleTimelineWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.5 : -0.5;
      handleTimelineZoom(timelineZoom + delta, e.clientX);
    } else {
      if (timelineScrollRef.current) {
        timelineScrollRef.current.scrollLeft += e.deltaY;
      }
    }
  };

  const handleSetIn = () => {
    const cur = parseFloat(currentTime.toFixed(1));
    setClipIn(cur);
    const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 0);
    if (clipOut === null || clipOut <= cur) {
      const defaultOut = totalDur > 0 ? Math.min(totalDur, cur + 30) : cur + 30;
      setClipOut(parseFloat(defaultOut.toFixed(1)));
    }
    setClipMark(cur);
    isPreviewingClipRegionRef.current = true;
  };

  const handleSetOut = () => {
    const cur = parseFloat(currentTime.toFixed(1));
    setClipOut(cur);
    if (clipIn === null || clipIn >= cur) {
      const defaultIn = Math.max(0, cur - 30);
      setClipIn(parseFloat(defaultIn.toFixed(1)));
    }
    setClipMark(clipIn !== null ? clipIn : Math.max(0, cur - 30));
    isPreviewingClipRegionRef.current = true;
  };

  const handleToggleClipMark = () => {
    if (hasClipMark) {
      handleClearClip();
    } else {
      setCenteredClipRegion(currentTime);
    }
  };

  const handleClearClip = () => {
    setClipIn(null);
    setClipOut(null);
    setClipMark(null);
    isPreviewingClipRegionRef.current = false;
  };

  // Playhead auto-scroll to stay in view when zoomed in
  useEffect(() => {
    if (!autoScrollTimeline || timelineZoom <= 1.0 || !isPlaying || !timelineScrollRef.current) return;
    const container = timelineScrollRef.current;
    const currentPx = timeToPx(currentTime);
    const scrollLeft = container.scrollLeft;
    const clientWidth = container.clientWidth;
    const buffer = 50;

    if (currentPx > scrollLeft + clientWidth - buffer) {
      container.scrollLeft = currentPx - clientWidth + buffer;
    } else if (currentPx < scrollLeft + buffer) {
      container.scrollLeft = Math.max(0, currentPx - buffer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, isPlaying, timelineZoom, autoScrollTimeline]);

  const handleStartDragPeriod = (e: React.PointerEvent, periodNumber: number, handle: 'start' | 'end') => {
    e.preventDefault();
    dragPeriodNum.current = periodNumber;
    dragPeriodHandle.current = handle;
    dragInitialPeriods.current = JSON.parse(JSON.stringify(periodsRef.current));
    setActiveTimelineDrag(handle === 'start' ? 'period-start' : 'period-end');
  };

  const handleStartDragGoal = (
    e: React.PointerEvent,
    goalIdOrNumber: number | string,
    handle: 'start' | 'end' | 'window' = 'window'
  ) => {
    e.preventDefault();
    dragGoalNum.current = goalIdOrNumber;
    dragGoalHandle.current = handle;
    const g = gameRef.current?.goals?.find((item: any) => matchesGoalId(item, goalIdOrNumber));
    const gh = gameRef.current?.highlights?.find(
      (item: any) =>
        (g?.eventId && item.eventId === g.eventId) ||
        (item.goalNumber === g?.goalNumber && Math.abs((item.videoTimestamp || 0) - (g?.videoTimestamp || 0)) <= 35)
    );
    const start = g?.clipStartSeconds !== undefined
      ? g.clipStartSeconds
      : (gh?.clipStartSeconds !== undefined ? gh.clipStartSeconds : Math.max(0, (g?.videoTimestamp || 0) - 15));
    const end = g?.clipEndSeconds !== undefined
      ? g.clipEndSeconds
      : (gh?.clipEndSeconds !== undefined ? gh.clipEndSeconds : Math.max(start + 5, (g?.videoTimestamp || 0) + 5));
    dragInitialGoal.current = {
      goalId: goalIdOrNumber,
      goalNumber: g?.goalNumber || (typeof goalIdOrNumber === 'number' ? goalIdOrNumber : 1),
      videoTimestamp: g?.videoTimestamp || 0,
      clipStartSeconds: start,
      clipEndSeconds: end,
      durationSeconds: Math.max(0, end - start),
    };
    dragInitialGoalTime.current = g?.videoTimestamp || 0;
    dragStartX.current = e.clientX;
    if (handle === 'start') {
      setActiveTimelineDrag('goal-start');
    } else if (handle === 'end') {
      setActiveTimelineDrag('goal-end');
    } else {
      setActiveTimelineDrag('goal-window');
    }
  };

  const handleStartDragClip = (
    e: React.PointerEvent,
    clipId: string,
    handle: 'start' | 'end' | 'window'
  ) => {
    e.preventDefault();
    const clip = timelineEventsRef.current.find((item) => item.id === clipId && item.eventType === 'clip') as TimelineClip | undefined;
    if (!clip) return;
    dragClipId.current = clipId;
    dragClipHandle.current = handle;
    dragInitialClip.current = JSON.parse(JSON.stringify(clip));
    dragStartX.current = e.clientX;
    if (handle === 'start') {
      setActiveTimelineDrag('clip-start');
    } else if (handle === 'end') {
      setActiveTimelineDrag('clip-end');
    } else {
      setActiveTimelineDrag('clip-window');
    }
  };

  const handleStartDragNote = (e: React.PointerEvent, noteId: string) => {
    e.preventDefault();
    const note = timelineEventsRef.current.find((item) => item.id === noteId && item.eventType === 'note') as TimelineNote | undefined;
    if (!note) return;
    dragNoteId.current = noteId;
    dragInitialNoteTime.current = note.time;
    setActiveTimelineDrag('note');
  };

  // Window pointer listeners for timeline dragging (playhead, trim handles, trim window, period handles, goals, clips)
  useEffect(() => {
    if (!activeTimelineDrag) return;

    const handlePointerMove = (e: PointerEvent) => {
      const time = getTimeFromClientX(e.clientX);
      const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 0);

      if (activeTimelineDrag === 'playhead') {
        if (videoRef.current && !videoRef.current.paused && !wasPlayingBeforeScrubRef.current) {
          wasPlayingBeforeScrubRef.current = true;
          videoRef.current.pause();
          setIsPlaying(false);
        }
        handleSeek(time, true);
      } else if (activeTimelineDrag === 'start') {
        const endVal = rangeClipEndRef.current > 0 ? rangeClipEndRef.current : (totalDur > 0 ? totalDur : 100);
        const newStart = Math.max(0, Math.min(time, endVal - 0.5));
        setClipIn(parseFloat(newStart.toFixed(1)));
        setClipOut(endVal);
        setClipMark(parseFloat(newStart.toFixed(1)));
        // Moving start handle adjusts clip boundary without moving playhead
      } else if (activeTimelineDrag === 'end') {
        const startVal = rangeClipStartRef.current;
        const maxDur = totalDur > 0 ? totalDur : Math.max(time, 100);
        const newEnd = Math.min(maxDur, Math.max(time, startVal + 0.5));
        setClipIn(startVal);
        setClipOut(parseFloat(newEnd.toFixed(1)));
        setClipMark(startVal);
        // Moving end handle adjusts clip boundary without moving playhead
      } else if (activeTimelineDrag === 'window') {
        const deltaX = e.clientX - dragStartX.current;
        const deltaTime = (deltaX / trackWidth) * (duration || 1);
        const clipLen = dragInitialOut.current - dragInitialIn.current;

        const maxDur = totalDur > 0 ? totalDur : Math.max(dragInitialOut.current, 100);

        let newStart = dragInitialIn.current + deltaTime;
        let newEnd = dragInitialOut.current + deltaTime;

        if (newStart < 0) {
          newStart = 0;
          newEnd = Math.min(maxDur, clipLen);
        } else if (newEnd > maxDur) {
          newEnd = maxDur;
          newStart = Math.max(0, maxDur - clipLen);
        }

        setClipIn(parseFloat(newStart.toFixed(1)));
        setClipOut(parseFloat(newEnd.toFixed(1)));
        setClipMark(parseFloat(newStart.toFixed(1)));
        // Moving window adjusts clip boundary without moving playhead
      } else if (activeTimelineDrag === 'period-start') {
        const pNum = dragPeriodNum.current;
        if (pNum === null) return;
        const curList = periodsRef.current;
        const idx = curList.findIndex((p) => p.period === pNum);
        if (idx === -1) return;
        const cur = curList[idx];
        const minVal = 0;
        const maxVal = cur.endTime - 5;
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;

        // Magnetic snap to playhead if within 2.5 seconds
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }

        // Do not update playhead position during dragging so user keeps reference frame and avoids network seeks
        setPeriods((prev) =>
          prev.map((p) => (p.period === pNum ? { ...p, startTime: clamped, isCustom: true } : p))
        );
      } else if (activeTimelineDrag === 'period-end') {
        const pNum = dragPeriodNum.current;
        if (pNum === null) return;
        const curList = periodsRef.current;
        const idx = curList.findIndex((p) => p.period === pNum);
        if (idx === -1) return;
        const cur = curList[idx];
        const minVal = cur.startTime + 5;
        const maxVal = totalDur > 0 ? totalDur : cur.startTime + 7200;
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;

        // Magnetic snap to playhead if within 2.5 seconds
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }

        // Do not update playhead position during dragging
        setPeriods((prev) =>
          prev.map((p) => (p.period === pNum ? { ...p, endTime: clamped, isCustom: true } : p))
        );
      } else if (activeTimelineDrag === 'goal-start') {
        const gNum = dragGoalNum.current;
        if (gNum === null) return;
        const cur = gameRef.current?.goals?.find((g: any) => matchesGoalId(g, gNum));
        if (!cur) return;
        const curEnd = cur.clipEndSeconds !== undefined
          ? cur.clipEndSeconds
          : (dragInitialGoal.current?.clipEndSeconds ?? (cur.videoTimestamp || 0) + 5);
        const minVal = 0;
        const maxVal = Math.max(0, curEnd - 1);
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }
        const newDur = Math.round((curEnd - clamped) * 10) / 10;
        setGame((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            goals: (prev.goals || []).map((g) =>
              matchesGoalId(g, gNum)
                ? {
                    ...g,
                    clipStartSeconds: clamped,
                    clipEndSeconds: curEnd,
                    durationSeconds: newDur,
                    videoTimestamp: Math.max(clamped, Math.min(curEnd, g.videoTimestamp || clamped)),
                  }
                : g
            ),
          };
        });
      } else if (activeTimelineDrag === 'goal-end') {
        const gNum = dragGoalNum.current;
        if (gNum === null) return;
        const cur = gameRef.current?.goals?.find((g: any) => matchesGoalId(g, gNum));
        if (!cur) return;
        const curStart = cur.clipStartSeconds !== undefined
          ? cur.clipStartSeconds
          : (dragInitialGoal.current?.clipStartSeconds ?? Math.max(0, (cur.videoTimestamp || 0) - 15));
        const minVal = curStart + 1;
        const maxVal = totalDur > 0 ? totalDur : Math.max(curStart + 1200, 100);
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }
        const newDur = Math.round((clamped - curStart) * 10) / 10;
        setGame((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            goals: (prev.goals || []).map((g) =>
              matchesGoalId(g, gNum)
                ? {
                    ...g,
                    clipStartSeconds: curStart,
                    clipEndSeconds: clamped,
                    durationSeconds: newDur,
                    videoTimestamp: Math.max(curStart, Math.min(clamped, g.videoTimestamp || clamped)),
                  }
                : g
            ),
          };
        });
      } else if (activeTimelineDrag === 'goal-window' || activeTimelineDrag === 'goal') {
        const gNum = dragGoalNum.current;
        if (gNum === null) return;
        const init = dragInitialGoal.current;
        const initStart = init?.clipStartSeconds ?? 0;
        const initEnd = init?.clipEndSeconds ?? 30;
        const initDuration = init?.durationSeconds ?? Math.max(1, initEnd - initStart);
        const initTs = init?.videoTimestamp ?? (initStart + initEnd) / 2;
        const deltaX = e.clientX - dragStartX.current;
        const deltaTime = (deltaX / trackWidth) * (duration || 1);
        const maxDur = totalDur > 0 ? totalDur : Math.max(initEnd, 100);

        let newStart = initStart + deltaTime;
        let newEnd = newStart + initDuration;

        if (newStart < 0) {
          newStart = 0;
          newEnd = Math.min(maxDur, initDuration);
        } else if (newEnd > maxDur) {
          newEnd = maxDur;
          newStart = Math.max(0, maxDur - initDuration);
        }

        newStart = Math.round(newStart * 10) / 10;
        newEnd = Math.round(newEnd * 10) / 10;
        let newTs = Math.round((initTs + deltaTime) * 10) / 10;
        newTs = Math.max(newStart, Math.min(newEnd, newTs));

        setGame((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            goals: (prev.goals || []).map((g) =>
              matchesGoalId(g, gNum)
                ? {
                    ...g,
                    clipStartSeconds: newStart,
                    clipEndSeconds: newEnd,
                    durationSeconds: Math.round((newEnd - newStart) * 10) / 10,
                    videoTimestamp: newTs,
                    videoTimestampStr: formatSeconds(newTs),
                  }
                : g
            ),
          };
        });
      } else if (activeTimelineDrag === 'clip-start') {
        const cId = dragClipId.current;
        if (!cId) return;
        const cur = timelineEventsRef.current.find((item) => item.id === cId && item.eventType === 'clip') as TimelineClip | undefined;
        if (!cur) return;
        const minVal = 0;
        const maxVal = cur.endTime - 1;
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }
        setTimelineEvents((prev) =>
          prev.map((ev) => (ev.id === cId && ev.eventType === 'clip' ? ({ ...ev, startTime: clamped, duration: Math.round((ev.endTime - clamped) * 10) / 10 } as TimelineClip) : ev))
        );
      } else if (activeTimelineDrag === 'clip-end') {
        const cId = dragClipId.current;
        if (!cId) return;
        const cur = timelineEventsRef.current.find((item) => item.id === cId && item.eventType === 'clip') as TimelineClip | undefined;
        if (!cur) return;
        const minVal = cur.startTime + 1;
        const maxVal = totalDur > 0 ? totalDur : Math.max(cur.startTime + 1200, 100);
        let clamped = Math.round(Math.max(minVal, Math.min(maxVal, time)) * 10) / 10;
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= minVal && currentTime <= maxVal) {
          clamped = Math.round(currentTime * 10) / 10;
        }
        setTimelineEvents((prev) =>
          prev.map((ev) => (ev.id === cId && ev.eventType === 'clip' ? ({ ...ev, endTime: clamped, duration: Math.round((clamped - ev.startTime) * 10) / 10 } as TimelineClip) : ev))
        );
      } else if (activeTimelineDrag === 'clip-window') {
        const cId = dragClipId.current;
        if (!cId) return;
        const deltaX = e.clientX - dragStartX.current;
        const deltaTime = (deltaX / trackWidth) * (duration || 1);
        const clipLen = dragInitialClip.current?.duration ?? (dragInitialClip.current ? dragInitialClip.current.endTime - dragInitialClip.current.startTime : 30);
        const maxDur = totalDur > 0 ? totalDur : Math.max(dragInitialClip.current?.endTime || 30, 100);

        let newStart = (dragInitialClip.current?.startTime ?? 0) + deltaTime;
        let newEnd = newStart + clipLen;

        if (newStart < 0) {
          newStart = 0;
          newEnd = Math.min(maxDur, clipLen);
        } else if (newEnd > maxDur) {
          newEnd = maxDur;
          newStart = Math.max(0, maxDur - clipLen);
        }

        newStart = Math.round(newStart * 10) / 10;
        newEnd = Math.round(newEnd * 10) / 10;
        setTimelineEvents((prev) =>
          prev.map((ev) => (ev.id === cId && ev.eventType === 'clip' ? ({ ...ev, startTime: newStart, endTime: newEnd } as TimelineClip) : ev))
        );
      } else if (activeTimelineDrag === 'note') {
        const nId = dragNoteId.current;
        if (!nId) return;
        const maxDur = totalDur > 0 ? totalDur : 7200;
        let clamped = Math.round(Math.max(0, Math.min(maxDur, time)) * 10) / 10;
        if (Math.abs(clamped - currentTime) <= 2.5 && currentTime >= 0 && currentTime <= maxDur) {
          clamped = Math.round(currentTime * 10) / 10;
        }
        setTimelineEvents((prev) =>
          prev.map((ev) => (ev.id === nId && ev.eventType === 'note' ? ({ ...ev, time: clamped } as TimelineNote) : ev))
        );
      }
    };

    const handlePointerUp = () => {
      if (activeTimelineDrag === 'period-start' || activeTimelineDrag === 'period-end') {
        dragPeriodNum.current = null;
        dragPeriodHandle.current = null;
      } else if (
        activeTimelineDrag === 'goal' ||
        activeTimelineDrag === 'goal-start' ||
        activeTimelineDrag === 'goal-end' ||
        activeTimelineDrag === 'goal-window'
      ) {
        dragGoalNum.current = null;
        dragGoalHandle.current = null;
        dragInitialGoal.current = null;
      } else if (activeTimelineDrag === 'clip-start' || activeTimelineDrag === 'clip-end' || activeTimelineDrag === 'clip-window') {
        dragClipId.current = null;
        dragClipHandle.current = null;
        dragInitialClip.current = null;
      } else if (activeTimelineDrag === 'note') {
        dragNoteId.current = null;
      } else if (activeTimelineDrag === 'playhead') {
        const finalTime = pendingScrubTimeRef.current !== null ? pendingScrubTimeRef.current : currentTime;
        handleSeek(finalTime, false);
        syncUrlTimestamp(finalTime, selectedFeed);
        if (wasPlayingBeforeScrubRef.current) {
          wasPlayingBeforeScrubRef.current = false;
          if (videoRef.current) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          }
        }
      }

      setActiveTimelineDrag(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimelineDrag, duration, trackWidth, game]);

  const handleUndo = async () => {
    if (undoStack.length === 0 || !game) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    setRedoStack((prev) => [...prev, action]);

    if (action.type === 'period') {
      setPeriods(action.prev);
      await updateGamePeriods(game.gameId, action.prev);
      const updated = await fetchGameVideoDetails(game.gameId);
      setGame(updated);
    } else if (action.type === 'goal') {
      const prevGoal: GoalEventData = action.prev;
      const targetId = getGoalIdentifier(prevGoal);
      const prevTs = prevGoal.videoTimestamp;
      setGame((prev) => {
        if (!prev) return null;
        const mapped = (prev.goals || []).map((g) =>
          matchesGoalId(g, targetId)
            ? {
                ...g,
                ...prevGoal,
                videoTimestamp: prevTs,
                videoTimestampStr: formatSeconds(prevTs),
                isLocked: true,
              }
            : g
        );
        return {
          ...prev,
          goals: sequenceGoalsChronologically(mapped),
        };
      });
      const goalPayload: Partial<GoalEventData> = {
        videoTimestamp: prevTs,
        ...(prevGoal.scoringTeam !== undefined ? { scoringTeam: prevGoal.scoringTeam } : {}),
        ...(prevGoal.clipStartSeconds !== undefined ? { clipStartSeconds: prevGoal.clipStartSeconds } : {}),
        ...(prevGoal.clipEndSeconds !== undefined ? { clipEndSeconds: prevGoal.clipEndSeconds } : {}),
        ...(prevGoal.durationSeconds !== undefined ? { durationSeconds: prevGoal.durationSeconds } : {}),
        ...(prevGoal.period !== undefined ? { period: prevGoal.period } : {}),
        ...(prevGoal.scoreAfter !== undefined ? { scoreAfter: prevGoal.scoreAfter } : {}),
        ...(prevGoal.gameClock !== undefined ? { gameClock: prevGoal.gameClock } : {}),
        ...(prevGoal.scorer !== undefined ? { scorer: prevGoal.scorer } : {}),
        ...(prevGoal.scorerJersey !== undefined ? { scorerJersey: prevGoal.scorerJersey } : {}),
        ...(prevGoal.assists !== undefined ? { assists: prevGoal.assists } : {}),
        ...(prevGoal.description !== undefined ? { description: prevGoal.description } : {}),
        ...(prevGoal.isModified !== undefined ? { isModified: prevGoal.isModified } : {}),
        isLocked: true,
      };
      await updateGameGoal(game.gameId, targetId, goalPayload);
    } else if (action.type === 'add_goal') {
      const addedGoal: GoalEventData = action.next;
      if (addedGoal) {
        const targetId = String(getGoalIdentifier(addedGoal));
        await deleteGameEvent(game.gameId, targetId);
        const updated = await fetchGameVideoDetails(game.gameId);
        setGame(updated);
        setSelectedTimelineItem(null);
      }
    } else if (action.type === 'delete_goal') {
      const deletedGoal: GoalEventData = action.prev;
      if (deletedGoal) {
        await addGameEvent(game.gameId, {
          type: 'goal',
          scoringTeam: deletedGoal.scoringTeam || focalTeamShort,
          videoTimestamp: deletedGoal.videoTimestamp,
          videoTimestampStr: deletedGoal.videoTimestampStr || formatSeconds(deletedGoal.videoTimestamp),
          clipStartSeconds: deletedGoal.clipStartSeconds,
          clipEndSeconds: deletedGoal.clipEndSeconds,
          durationSeconds: deletedGoal.durationSeconds,
          period: deletedGoal.period,
          description: deletedGoal.description,
          scorer: deletedGoal.scorer,
          scorerJersey: deletedGoal.scorerJersey,
          assists: deletedGoal.assists,
          scoreAfter: deletedGoal.scoreAfter,
          gameClock: deletedGoal.gameClock,
        });
        const updated = await fetchGameVideoDetails(game.gameId);
        setGame(updated);
      }
    } else if (action.type === 'convert') {
      const gId = gameId || game.gameId;
      if (action.prev.type === 'goal') {
        if (action.next.type === 'clip' || action.next.type === 'note') {
          await deleteTimelineEvent(gId, action.next.data.id);
          setTimelineEvents((prev) => prev.filter((e) => e.id !== action.next.data.id));
        }
        await addGameEvent(gId, {
          type: 'goal',
          scoringTeam: action.prev.data.team || action.prev.data.scoringTeam || focalTeamShort,
          videoTimestamp: action.prev.data.videoTimestamp,
          videoTimestampStr: action.prev.data.videoTimestampStr || formatSeconds(action.prev.data.videoTimestamp),
          clipStartSeconds: action.prev.data.clipStartSeconds,
          clipEndSeconds: action.prev.data.clipEndSeconds,
          durationSeconds: action.prev.data.durationSeconds,
          period: action.prev.data.period,
          description: action.prev.data.description,
          scorer: action.prev.data.scorer,
          scorerJersey: action.prev.data.scorerJersey,
          assists: action.prev.data.assists,
          scoreAfter: action.prev.data.scoreAfter,
          gameClock: action.prev.data.gameClock,
        });
        const updated = await fetchGameVideoDetails(gId);
        setGame(updated);
        setSelectedTimelineItem({
          type: 'goal',
          goalNumber: action.prev.data.goalNumber,
          ...(action.prev.data.eventId ? { eventId: action.prev.data.eventId } : {}),
        });
      } else if (action.prev.type === 'clip') {
        if (action.next.type === 'goal') {
          await deleteGameEvent(gId, action.next.data.eventId || action.next.data.id || `goal-${action.next.data.goalNumber}`);
          const updated = await fetchGameVideoDetails(gId);
          setGame(updated);
        } else if (action.next.type === 'note') {
          await deleteTimelineEvent(gId, action.next.data.id);
          setTimelineEvents((prev) => prev.filter((e) => e.id !== action.next.data.id));
        }
        const restored = await createTimelineEvent(gId, {
          eventType: 'clip',
          startTime: action.prev.data.startTime,
          endTime: action.prev.data.endTime,
          duration: action.prev.data.duration,
          title: action.prev.data.title,
          notes: action.prev.data.notes,
          generalAccess: action.prev.data.generalAccess,
          shares: action.prev.data.shares,
        });
        setTimelineEvents((prev) => [...prev, restored]);
        setSelectedTimelineItem({ type: 'clip', id: restored.id });
      } else if (action.prev.type === 'note') {
        if (action.next.type === 'goal') {
          await deleteGameEvent(gId, action.next.data.eventId || action.next.data.id || `goal-${action.next.data.goalNumber}`);
          const updated = await fetchGameVideoDetails(gId);
          setGame(updated);
        } else if (action.next.type === 'clip') {
          await deleteTimelineEvent(gId, action.next.data.id);
          setTimelineEvents((prev) => prev.filter((e) => e.id !== action.next.data.id));
        }
        const restored = await createTimelineEvent(gId, {
          eventType: 'note',
          time: action.prev.data.time,
          text: action.prev.data.text,
          generalAccess: action.prev.data.generalAccess,
          shares: action.prev.data.shares,
        });
        setTimelineEvents((prev) => [...prev, restored]);
        setSelectedTimelineItem({ type: 'note', id: restored.id });
      }
    }
  };

  const handleRedo = async () => {
    if (redoStack.length === 0 || !game) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [...prev, action]);

    if (action.type === 'period') {
      setPeriods(action.next);
      const res = await updateGamePeriods(game.gameId, action.next);
      const serverPeriods = res?.periods && res.periods.length > 0 ? res.periods : action.next;
      setPeriods(serverPeriods);
      setGame((prev) => (prev ? { ...prev, periods: serverPeriods } : prev));
    } else if (action.type === 'goal') {
      const nextGoal: GoalEventData = action.next;
      const targetId = getGoalIdentifier(nextGoal);
      const nextTs = nextGoal.videoTimestamp;
      setGame((prev) => {
        if (!prev) return null;
        const mapped = (prev.goals || []).map((g) =>
          matchesGoalId(g, targetId)
            ? {
                ...g,
                ...nextGoal,
                videoTimestamp: nextTs,
                videoTimestampStr: formatSeconds(nextTs),
                isLocked: true,
              }
            : g
        );
        return {
          ...prev,
          goals: sequenceGoalsChronologically(mapped),
        };
      });
      const goalPayload: Partial<GoalEventData> = {
        videoTimestamp: nextTs,
        ...(nextGoal.scoringTeam !== undefined ? { scoringTeam: nextGoal.scoringTeam } : {}),
        ...(nextGoal.clipStartSeconds !== undefined ? { clipStartSeconds: nextGoal.clipStartSeconds } : {}),
        ...(nextGoal.clipEndSeconds !== undefined ? { clipEndSeconds: nextGoal.clipEndSeconds } : {}),
        ...(nextGoal.durationSeconds !== undefined ? { durationSeconds: nextGoal.durationSeconds } : {}),
        ...(nextGoal.period !== undefined ? { period: nextGoal.period } : {}),
        ...(nextGoal.scoreAfter !== undefined ? { scoreAfter: nextGoal.scoreAfter } : {}),
        ...(nextGoal.gameClock !== undefined ? { gameClock: nextGoal.gameClock } : {}),
        ...(nextGoal.scorer !== undefined ? { scorer: nextGoal.scorer } : {}),
        ...(nextGoal.scorerJersey !== undefined ? { scorerJersey: nextGoal.scorerJersey } : {}),
        ...(nextGoal.assists !== undefined ? { assists: nextGoal.assists } : {}),
        ...(nextGoal.description !== undefined ? { description: nextGoal.description } : {}),
        ...(nextGoal.isModified !== undefined ? { isModified: nextGoal.isModified } : {}),
        isLocked: true,
      };
      await updateGameGoal(game.gameId, targetId, goalPayload);
    } else if (action.type === 'add_goal') {
      const addedGoal: GoalEventData = action.next;
      if (addedGoal) {
        await addGameEvent(game.gameId, {
          type: 'goal',
          scoringTeam: addedGoal.scoringTeam || focalTeamShort,
          videoTimestamp: addedGoal.videoTimestamp,
          videoTimestampStr: addedGoal.videoTimestampStr || formatSeconds(addedGoal.videoTimestamp),
          clipStartSeconds: addedGoal.clipStartSeconds,
          clipEndSeconds: addedGoal.clipEndSeconds,
          durationSeconds: addedGoal.durationSeconds,
          period: addedGoal.period,
          description: addedGoal.description,
          scorer: addedGoal.scorer,
          scorerJersey: addedGoal.scorerJersey,
          assists: addedGoal.assists,
          scoreAfter: addedGoal.scoreAfter,
          gameClock: addedGoal.gameClock,
        });
        const updated = await fetchGameVideoDetails(game.gameId);
        setGame(updated);
      }
    } else if (action.type === 'delete_goal') {
      const deletedGoal: GoalEventData = action.prev;
      if (deletedGoal) {
        await deleteGameEvent(game.gameId, String(getGoalIdentifier(deletedGoal)));
        const updated = await fetchGameVideoDetails(game.gameId);
        setGame(updated);
        setSelectedTimelineItem(null);
      }
    }
  };

  // Timeline Events CRUD & Share & Download Handlers
  const handleCreateNote = async () => {
    if (!gameId) return;
    const time = Math.round(currentTime * 10) / 10;
    try {
      const created = await createTimelineEvent(gameId, {
        eventType: 'note',
        time,
        text: '',
        generalAccess: 'restricted',
        shares: [],
      });
      setTimelineEvents((prev) => [...prev, created]);
      setSelectedTimelineItem({ type: 'note', id: created.id });
      setDownloadSnackbar({
        text: `Note created at ${formatSeconds(time)}`,
        severity: 'success',
      });
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to create note: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const handleCreateClip = async () => {
    if (!gameId) return;
    const startTime = Math.round(currentTime * 10) / 10;
    const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 7200);
    const endTime = Math.min(totalDur, Math.round((currentTime + 30) * 10) / 10);
    const clipDur = Math.round((endTime - startTime) * 10) / 10;
    try {
      const created = await createTimelineEvent(gameId, {
        eventType: 'clip',
        startTime,
        endTime,
        duration: clipDur,
        title: 'Custom Clip',
        notes: '',
        generalAccess: 'restricted',
        shares: [],
      });
      setTimelineEvents((prev) => [...prev, created]);
      setSelectedTimelineItem({ type: 'clip', id: created.id });
      setDownloadSnackbar({
        text: `30s Clip created at ${formatSeconds(startTime)}`,
        severity: 'success',
      });
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to create clip: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const handleUpdateNote = async (id: string, updates: Partial<TimelineNote>) => {
    if (!gameId) return;
    try {
      const updated = await updateTimelineEvent(gameId, id, updates);
      setTimelineEvents((prev) =>
        prev.map((ev) => (ev.id === id ? ({ ...ev, ...updated } as TimelineEventItem) : ev))
      );
      setUnlockedTimelineItems((prev) =>
        prev.filter((item) => !(item.type === 'note' && item.id === id))
      );
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to update note: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  };

  const handleUpdateClip = async (id: string, updates: Partial<TimelineClip>) => {
    if (!gameId) return;
    try {
      const updated = await updateTimelineEvent(gameId, id, updates);
      setTimelineEvents((prev) =>
        prev.map((ev) => (ev.id === id ? ({ ...ev, ...updated } as TimelineEventItem) : ev))
      );
      setUnlockedTimelineItems((prev) =>
        prev.filter((item) => !(item.type === 'clip' && item.id === id))
      );
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to update clip: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  };

  const handleDeleteTimelineEvent = async (id: string) => {
    if (!gameId) return;
    try {
      try {
        await deleteTimelineEvent(gameId, id);
      } catch (delErr: any) {
        // Fallback in case the item was originally added to manualEvents
        await deleteGameEvent(gameId, id);
      }
      setTimelineEvents((prev) => prev.filter((ev) => ev.id !== id));
      setGame((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          manualEvents: (prev.manualEvents || []).filter((me) => me.id !== id),
          goals: (prev.goals || []).filter((g) => g.id !== id && g.eventId !== id),
        };
      });
      if (
        (selectedTimelineItem?.type === 'note' && selectedTimelineItem.id === id) ||
        (selectedTimelineItem?.type === 'clip' && selectedTimelineItem.id === id)
      ) {
        setSelectedTimelineItem(null);
      }
      setUnlockedTimelineItems((prev) => prev.filter((item) => item.id !== id));
      setDownloadSnackbar({
        text: 'Timeline item deleted successfully.',
        severity: 'info',
      });
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to delete timeline event: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  };

  const handleConvertEventType = async (
    sourceType: 'goal' | 'clip' | 'note',
    sourceId: string | number,
    targetType: 'goal' | 'clip' | 'note'
  ) => {
    if (!game || !gameId) return;
    if (sourceType === targetType) return;

    try {
      if (sourceType === 'goal') {
        const goal = (game.goals || []).find((g) => matchesGoalId(g, sourceId));
        if (!goal) return;

        const gh = (game.highlights || []).find(
          (h) =>
            (goal.eventId && h.eventId === goal.eventId) ||
            (h.goalNumber === goal.goalNumber && Math.abs((h.videoTimestamp || 0) - (goal.videoTimestamp || 0)) <= 35)
        );
        const clipStart = goal.clipStartSeconds !== undefined
          ? goal.clipStartSeconds
          : (gh?.clipStartSeconds !== undefined ? gh.clipStartSeconds : Math.max(0, goal.videoTimestamp - 15));
        const clipEnd = goal.clipEndSeconds !== undefined
          ? goal.clipEndSeconds
          : (gh?.clipEndSeconds !== undefined ? gh.clipEndSeconds : goal.videoTimestamp + 5);
        const teamLabel = getGoalTeamName(goal, isJetsHome, game.opponent, game.targetTeam);

        if (targetType === 'clip') {
          const created = await createTimelineEvent(gameId, {
            eventType: 'clip',
            startTime: Math.round(clipStart * 10) / 10,
            endTime: Math.round(clipEnd * 10) / 10,
            duration: Math.round(Math.max(0, clipEnd - clipStart) * 10) / 10,
            title: `Goal Clip: ${teamLabel}`,
            notes: goal.description || `Converted from Goal #${goal.goalNumber}`,
            generalAccess: 'public',
            shares: [],
          });
          created.eventType = (created.eventType || (created as any).type || 'clip') as 'clip';
          await deleteGameEvent(gameId, goal.eventId || goal.id || `goal-${goal.goalNumber}`);
          const updated = await fetchGameVideoDetails(gameId);
          setGame(updated);
          setTimelineEvents((prev) => [...prev, created]);
          setSelectedTimelineItem({ type: 'clip', id: created.id });
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Goal #${goal.goalNumber} to Clip`,
              prev: { type: 'goal', data: goal, highlight: gh },
              next: { type: 'clip', data: created },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Goal #${goal.goalNumber} to Clip`,
            severity: 'success',
          });
        } else if (targetType === 'note') {
          const created = await createTimelineEvent(gameId, {
            eventType: 'note',
            time: Math.round(goal.videoTimestamp * 10) / 10,
            text: `Goal: ${teamLabel} - ${goal.description || `Goal #${goal.goalNumber}`}`,
            generalAccess: 'public',
            shares: [],
          });
          created.eventType = (created.eventType || (created as any).type || 'note') as 'note';
          await deleteGameEvent(gameId, goal.eventId || `goal-${goal.goalNumber}`);
          const updated = await fetchGameVideoDetails(gameId);
          setGame(updated);
          setTimelineEvents((prev) => [...prev, created]);
          setSelectedTimelineItem({ type: 'note', id: created.id });
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Goal #${goal.goalNumber} to Note`,
              prev: { type: 'goal', data: goal, highlight: gh },
              next: { type: 'note', data: created },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Goal #${goal.goalNumber} to Note`,
            severity: 'success',
          });
        }
      } else if (sourceType === 'clip') {
        const clip = timelineEvents.find((e) => e.id === String(sourceId) && e.eventType === 'clip') as TimelineClip | undefined;
        if (!clip) return;

        if (targetType === 'goal') {
          const ts = clip.startTime;
          let inferredPeriod = 1;
          if (periodsRef.current && periodsRef.current.length > 0) {
            const matchP = periodsRef.current.find((p) => ts >= p.startTime && ts <= p.endTime);
            if (matchP) inferredPeriod = matchP.period;
          }
          await addGameEvent(gameId, {
            type: 'goal',
            scoringTeam: focalTeamShort,
            videoTimestamp: Math.round(ts * 10) / 10,
            videoTimestampStr: formatSeconds(ts),
            clipStartSeconds: clip.startTime,
            clipEndSeconds: clip.endTime,
            durationSeconds: clip.duration,
            description: clip.title ? `${clip.title}${clip.notes ? ' - ' + clip.notes : ''}` : clip.notes || '',
            period: inferredPeriod,
          });
          await deleteTimelineEvent(gameId, clip.id);
          setTimelineEvents((prev) => prev.filter((e) => e.id !== clip.id));
          const updated = await fetchGameVideoDetails(gameId);
          setGame(updated);
          const updatedGoals = updated.goals || [];
          const newGoal = updatedGoals.length > 0 ? updatedGoals[updatedGoals.length - 1] : undefined;
          if (newGoal) {
            setSelectedTimelineItem({ type: 'goal', goalNumber: newGoal.goalNumber });
          } else {
            setSelectedTimelineItem(null);
          }
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Clip to Goal`,
              prev: { type: 'clip', data: clip },
              next: { type: 'goal', data: newGoal },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Clip to Goal`,
            severity: 'success',
          });
        } else if (targetType === 'note') {
          const created = await createTimelineEvent(gameId, {
            eventType: 'note',
            time: Math.round(clip.startTime * 10) / 10,
            text: clip.title ? `${clip.title}${clip.notes ? ' - ' + clip.notes : ''}` : clip.notes || 'Converted Note',
            generalAccess: 'public',
            shares: [],
          });
          created.eventType = (created.eventType || (created as any).type || 'note') as 'note';
          await deleteTimelineEvent(gameId, clip.id);
          setTimelineEvents((prev) => [...prev.filter((e) => e.id !== clip.id), created]);
          setSelectedTimelineItem({ type: 'note', id: created.id });
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Clip to Note`,
              prev: { type: 'clip', data: clip },
              next: { type: 'note', data: created },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Clip to Note`,
            severity: 'success',
          });
        }
      } else if (sourceType === 'note') {
        const note = timelineEvents.find((e) => e.id === String(sourceId) && e.eventType === 'note') as TimelineNote | undefined;
        if (!note) return;

        if (targetType === 'goal') {
          const ts = note.time;
          let inferredPeriod = 1;
          if (periodsRef.current && periodsRef.current.length > 0) {
            const matchP = periodsRef.current.find((p) => ts >= p.startTime && ts <= p.endTime);
            if (matchP) inferredPeriod = matchP.period;
          }
          await addGameEvent(gameId, {
            type: 'goal',
            scoringTeam: focalTeamShort,
            videoTimestamp: Math.round(ts * 10) / 10,
            videoTimestampStr: formatSeconds(ts),
            clipStartSeconds: Math.max(0, Math.round((ts - 15) * 10) / 10),
            clipEndSeconds: Math.round((ts + 5) * 10) / 10,
            durationSeconds: 20,
            description: note.text || '',
            period: inferredPeriod,
          });
          await deleteTimelineEvent(gameId, note.id);
          setTimelineEvents((prev) => prev.filter((e) => e.id !== note.id));
          const updated = await fetchGameVideoDetails(gameId);
          setGame(updated);
          const updatedGoals = updated.goals || [];
          const newGoal = updatedGoals.length > 0 ? updatedGoals[updatedGoals.length - 1] : undefined;
          if (newGoal) {
            setSelectedTimelineItem({ type: 'goal', goalNumber: newGoal.goalNumber });
          } else {
            setSelectedTimelineItem(null);
          }
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Note to Goal`,
              prev: { type: 'note', data: note },
              next: { type: 'goal', data: newGoal },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Note to Goal`,
            severity: 'success',
          });
        } else if (targetType === 'clip') {
          const startTime = Math.max(0, Math.round((note.time - 15) * 10) / 10);
          const totalDur = duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 7200);
          const endTime = Math.min(totalDur, Math.round((note.time + 15) * 10) / 10);
          const created = await createTimelineEvent(gameId, {
            eventType: 'clip',
            startTime,
            endTime,
            duration: Math.round((endTime - startTime) * 10) / 10,
            title: note.text ? (note.text.length > 30 ? `${note.text.slice(0, 30)}...` : note.text) : 'Custom Clip',
            notes: note.text || '',
            generalAccess: 'public',
            shares: [],
          });
          created.eventType = (created.eventType || (created as any).type || 'clip') as 'clip';
          await deleteTimelineEvent(gameId, note.id);
          setTimelineEvents((prev) => [...prev.filter((e) => e.id !== note.id), created]);
          setSelectedTimelineItem({ type: 'clip', id: created.id });
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'convert',
              description: `Converted Note to Clip`,
              prev: { type: 'note', data: note },
              next: { type: 'clip', data: created },
            },
          ]);
          setRedoStack([]);
          setDownloadSnackbar({
            text: `Converted Note to Clip`,
            severity: 'success',
          });
        }
      }
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Conversion failed: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
    }
  };

  const handleUpdateEventShares = async (shareData: {
    generalAccess: GeneralAccessLevel;
    publicRole: ShareAccessRole;
    shares: TimelineShareTarget[];
  }) => {
    if (!gameId || !shareTargetEvent) return;
    try {
      const updated = await updateTimelineEventShares(gameId, shareTargetEvent.id, shareData);
      setTimelineEvents((prev) =>
        prev.map((ev) => (ev.id === shareTargetEvent.id ? ({ ...ev, ...updated } as TimelineEventItem) : ev))
      );
      setShareTargetEvent((prev) => (prev ? ({ ...prev, ...updated } as TimelineEventItem) : null));
      setDownloadSnackbar({
        text: 'Sharing permissions updated successfully.',
        severity: 'success',
      });
    } catch (err: any) {
      setDownloadSnackbar({
        text: `Failed to update sharing: ${err.message || 'Unknown error'}`,
        severity: 'error',
      });
      throw err;
    }
  };

  const handleConfirmDownloadClipFeed = async (chosenFeed: 'auto' | 'pano' | 'both') => {
    if (!gameId || !downloadTargetClip) return;
    const clip = downloadTargetClip;
    setDownloadSnackbar({
      text: `Preparing "${clip.title}" (${clip.duration}s) for download... Please wait.`,
      severity: 'info',
    });
    try {
      const s = Math.max(0, Math.floor(clip.startTime));
      const e = Math.min(Math.ceil(duration || clip.endTime), Math.ceil(clip.endTime));
      const feedsToDownload: Array<'auto' | 'pano'> = chosenFeed === 'both' ? ['auto', 'pano'] : [chosenFeed];

      for (const feed of feedsToDownload) {
        const offset = game?.feedSync?.offsets?.[feed] ?? 0;
        const feedStart = Math.max(0, s + offset);
        const feedEnd = Math.max(feedStart + 1, e + offset);
        const blob = await downloadCustomClip(gameId, feedStart, feedEnd, feed, clip.title);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const slug = (clip.title || 'clip').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        a.download = `chicagojets_${gameId}_${feed}_${slug}_${s}s_${e}s.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      setDownloadSnackbar({
        text: `Clip "${clip.title}" successfully downloaded!`,
        severity: 'success',
      });
      setDownloadFeedDialogOpen(false);
      setDownloadTargetClip(null);
    } catch (err: any) {
      const msg = err.message || 'Failed to download clip feed.';
      setDownloadSnackbar({
        text: `Error downloading clip: ${msg}`,
        severity: 'error',
      });
    }
  };

  // Handle hotkeys (comma / period for frame stepping, Space for play/pause, Arrow keys for seek/speed, M for clip pin, I/O for In/Out, Ctrl+Z/Y for Undo/Redo, C for Clip, N for Note)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
      } else if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleRelativeSeek(e.shiftKey ? -30 : -10);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleRelativeSeek(e.shiftKey ? 30 : 10);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleSetPlaybackRate(Math.min(2.5, playbackRate + 0.25));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleSetPlaybackRate(Math.max(0.25, playbackRate - 0.25));
      } else if (e.key === ',') {
        stepFrame(-1);
      } else if (e.key === '.') {
        stepFrame(1);
      } else if (e.key.toLowerCase() === 'm') {
        handleToggleClipMark();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          handleToggleFullscreen();
        } else {
          handleClearClip();
        }
      } else if (e.key.toLowerCase() === 'f' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleToggleFullscreen();
      } else if (e.key.toLowerCase() === 'i') {
        if (clipperOpen && videoRef.current) {
          const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
          setClipStart(Math.floor(Math.max(0, videoRef.current.currentTime - curFeedOffset)));
        } else {
          handleSetIn();
        }
      } else if (e.key.toLowerCase() === 'o') {
        if (clipperOpen && videoRef.current) {
          const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
          setClipEnd(Math.ceil(Math.max(0, videoRef.current.currentTime - curFeedOffset)));
        } else {
          handleSetOut();
        }
      } else if (e.key.toLowerCase() === 'c' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleCreateClip();
      } else if (e.key.toLowerCase() === 'n' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleCreateNote();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipperOpen, isPlaying, duration, playbackRate, clipIn, clipOut, clipMark, hasClipMark, undoStack, redoStack, currentTime, gameId, isFullscreen]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
    if (videoRef.current.paused) {
      if (hasClipMark) {
        isPreviewingClipRegionRef.current = true;
        // Constrain playback to within marked download region
        const curTimeline = videoRef.current.currentTime - curFeedOffset;
        if (curTimeline < rangeClipStart || curTimeline >= rangeClipEnd - 0.1) {
          const targetVideoTime = Math.max(0, rangeClipStart + curFeedOffset);
          videoRef.current.currentTime = targetVideoTime;
          lastDispatchedSeekTimeRef.current = targetVideoTime;
          lastTargetTimelineTimeRef.current = rangeClipStart;
          setCurrentTime(rangeClipStart);
        }
      }
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      if (!isReelMode && game?.gameId) {
        const baselineT = Math.max(0, videoRef.current.currentTime - curFeedOffset);
        try {
          localStorage.setItem(`jets_playback_pos_${game.gameId}`, JSON.stringify({
            time: Math.floor(baselineT),
            feed: selectedFeed,
            updatedAt: Date.now(),
          }));
        } catch {}
        syncUrlTimestamp(baselineT, selectedFeed);
      }
    }
  };

  const stepFrame = (frames: number) => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setIsPlaying(false);
    suppressStoryboardOverlayRef.current = true;
    clearStoryboardOverlay();
    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
    const totalDur = videoRef.current.duration || duration || 3600;
    const baseVideoTime =
      videoRef.current.seeking && lastDispatchedSeekTimeRef.current !== null
        ? lastDispatchedSeekTimeRef.current
        : videoRef.current.currentTime;
    // Approximate frame duration at 30 fps
    const targetVideoTime = Math.max(0, Math.min(totalDur, baseVideoTime + frames * (1 / 30)));
    videoRef.current.currentTime = targetVideoTime;
    lastDispatchedSeekTimeRef.current = targetVideoTime;
    const clampedTimelineTime = Math.max(0, Math.min(totalDur, targetVideoTime - curFeedOffset));
    lastTargetTimelineTimeRef.current = clampedTimelineTime;
    setCurrentTime(clampedTimelineTime);
    if (zoom > 1.05) drawMinimapFrame();
  };

  const handleFeedChange = async (newFeed: 'auto' | 'pano') => {
    if (!newFeed || newFeed === selectedFeed || !game) return;
    setFeedNotice(null);
    const oldOffset = game?.feedSync?.offsets?.[selectedFeed] ?? 0;
    const newOffset = game?.feedSync?.offsets?.[newFeed] ?? 0;
    const rawVideoTime = videoRef.current ? videoRef.current.currentTime : (currentTime + oldOffset);
    const baselineTime = Math.max(0, rawVideoTime - oldOffset);
    const targetVideoTime = Math.max(0, baselineTime + newOffset);

    pendingSeekTimeRef.current = targetVideoTime;
    wasPlayingBeforeFeedChange.current = isPlaying;
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setSelectedFeed(newFeed);
    syncUrlTimestamp(baselineTime, newFeed);

    try {
      const streamInfo = await fetchVideoStreamUrl(game.gameId, newFeed).catch(() => null);
      const curToken = await getAuthToken();
      const newUrl = streamInfo?.direct
        ? streamInfo.url
        : (streamInfo?.url ? `${getBackendUrl()}${streamInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}` : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${newFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
      setActiveStreamUrl(newUrl);
      if (videoRef.current) {
        videoRef.current.src = newUrl;
        videoRef.current.load();
      }
    } catch (err) {
      const curToken = await getAuthToken();
      const fallbackUrl = `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${newFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`;
      setActiveStreamUrl(fallbackUrl);
      if (videoRef.current) {
        videoRef.current.src = fallbackUrl;
        videoRef.current.load();
      }
    }

    try {
      const scrubInfo = await fetchVideoStreamUrl(game.gameId, newFeed, 'scrub').catch(() => null);
      const curToken = await getAuthToken();
      const newScrubUrl = scrubInfo?.direct
        ? scrubInfo.url
        : (scrubInfo?.url ? `${getBackendUrl()}${scrubInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}` : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${newFeed}&variant=scrub${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
      setActiveScrubStreamUrl(newScrubUrl);
      if (scrubVideoRef.current) {
        scrubVideoRef.current.src = newScrubUrl;
        scrubVideoRef.current.load();
      }
    } catch {
      const curToken = await getAuthToken();
      const fallbackScrub = `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${newFeed}&variant=scrub${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`;
      setActiveScrubStreamUrl(fallbackScrub);
      if (scrubVideoRef.current) {
        scrubVideoRef.current.src = fallbackScrub;
        scrubVideoRef.current.load();
      }
    }
  };

  const handleOpenAddEvent = (presetType: string = 'goal') => {
    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
    const currentSec = videoRef.current ? Math.floor(Math.max(0, videoRef.current.currentTime - curFeedOffset)) : Math.floor(currentTime);
    setEventFormTimestamp(currentSec);
    setEventFormType(presetType);
    setEventFormTeam(focalTeamShort);
    setEventFormScore('');
    let inferredPeriod = 1;
    const existing = periodsRef.current || [];
    if (existing.length > 0) {
      const matchP = existing.find((p) => currentSec >= p.startTime && currentSec <= p.endTime);
      if (matchP) {
        inferredPeriod = matchP.period;
      } else {
        const lastP = existing[existing.length - 1];
        if (currentSec > lastP.endTime) {
          inferredPeriod = Math.max(lastP.period + 1, 4);
        }
      }
    }
    setEventFormPeriod(inferredPeriod);
    setEventFormPeriodLabel(inferredPeriod === 4 ? 'Overtime' : inferredPeriod > 4 ? `Overtime ${inferredPeriod - 3}` : `Period ${inferredPeriod}`);
    setEventFormPeriodStart(formatSeconds(currentSec));
    const defaultDur = inferredPeriod >= 4 ? 300 : 900;
    setEventFormPeriodEnd(formatSeconds(currentSec + defaultDur));
    setEventFormClock('');
    setEventFormDescription('');
    setEventError(null);
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!game) return;
    setSavingEvent(true);
    setEventError(null);
    try {
      if (eventFormType === 'period') {
        const pNum = Number(eventFormPeriod) || 4;
        const pLabel = eventFormPeriodLabel.trim() || (pNum === 4 ? 'Overtime' : pNum > 4 ? `Overtime ${pNum - 3}` : `Period ${pNum}`);
        const sTime = parseTimeInput(eventFormPeriodStart);
        const eTime = parseTimeInput(eventFormPeriodEnd);
        const pStart = Math.max(0, sTime);
        const pEnd = Math.max(pStart + 1, eTime || (pStart + 300));

        const newPeriod: GamePeriod = {
          period: pNum,
          label: pLabel,
          startTime: Math.round(pStart * 10) / 10,
          endTime: Math.round(pEnd * 10) / 10,
          actualClock: '',
          isCustom: true,
          isLocked: true,
        };

        const existingPeriods = periodsRef.current || [];
        const prevPeriods = [...existingPeriods];
        const filtered = existingPeriods.filter((p) => p.period !== pNum);
        const updatedPeriods = [...filtered, newPeriod].sort((a, b) => a.period - b.period || a.startTime - b.startTime);

        setPeriods(updatedPeriods);
        setGame((prev) => (prev ? { ...prev, periods: updatedPeriods } : prev));
        setUndoStack((prev) => [
          ...prev,
          {
            type: 'period',
            description: `Added ${newPeriod.label} (${formatSeconds(newPeriod.startTime)} - ${formatSeconds(newPeriod.endTime)})`,
            prev: prevPeriods,
            next: updatedPeriods,
          },
        ]);
        setRedoStack([]);

        const res = await updateGamePeriods(game.gameId, updatedPeriods);
        const serverPeriods = res?.periods && res.periods.length > 0 ? res.periods : updatedPeriods;
        setPeriods(serverPeriods);
        setGame((prev) => (prev ? { ...prev, periods: serverPeriods } : prev));
        setSelectedTimelineItem({ type: 'period', periodNumber: newPeriod.period });
        setEventDialogOpen(false);
        return;
      }

      const ts = Number(eventFormTimestamp) || 0;
      const defaultClipStart = Math.round(Math.max(0, ts - 15) * 10) / 10;
      const defaultClipEnd = Math.round((ts + 5) * 10) / 10;
      const res = await addGameEvent(game.gameId, {
        type: eventFormType,
        scoringTeam: eventFormTeam,
        scoreAfter: eventFormScore,
        period: Number(eventFormPeriod) || 1,
        gameClock: eventFormClock,
        videoTimestamp: ts,
        videoTimestampStr: formatSeconds(ts),
        ...(eventFormType === 'goal'
          ? {
              clipStartSeconds: defaultClipStart,
              clipEndSeconds: defaultClipEnd,
              durationSeconds: Math.round((defaultClipEnd - defaultClipStart) * 10) / 10,
            }
          : {}),
        description: eventFormDescription,
      });
      const updated = await fetchGameVideoDetails(game.gameId);
      setGame(updated);
      if (eventFormType === 'goal') {
        const createdId = res?.id || res?.event?.eventId || res?.event?.id;
        const matchedGoal = (updated?.goals || []).find(
          (g) =>
            (createdId && (g.eventId === createdId || g.id === createdId)) ||
            Math.abs((g.videoTimestamp || 0) - ts) < 0.5
        );
        if (matchedGoal) {
          const gEvtId = matchedGoal.eventId || matchedGoal.id;
          setSelectedTimelineItem({
            type: 'goal',
            goalNumber: matchedGoal.goalNumber,
            ...(gEvtId ? { eventId: gEvtId } : {}),
          });
          setUndoStack((prev) => [
            ...prev,
            {
              type: 'add_goal',
              description: `Added Goal #${matchedGoal.goalNumber}`,
              prev: null,
              next: matchedGoal,
            },
          ]);
          setRedoStack([]);
        }
      } else {
        const evtId = res?.id || res?.event?.id || `evt_${Date.now()}`;
        const newClip: TimelineClip = {
          id: evtId,
          eventType: 'clip',
          title: eventFormDescription || `${eventFormType.charAt(0).toUpperCase() + eventFormType.slice(1)}: ${eventFormTeam}`,
          startTime: Math.max(0, ts - 15),
          endTime: ts + 15,
          duration: 30,
          notes: eventFormDescription,
          generalAccess: 'public',
          shares: [],
          isLocked: true,
        };
        setTimelineEvents((prev) => {
          if (prev.some((e) => e.id === evtId)) return prev;
          return [...prev, newClip];
        });
        setSelectedTimelineItem({ type: 'clip', id: evtId });
      }
      setEventDialogOpen(false);
    } catch (err: any) {
      setEventError(err.message || 'Failed to save event marker');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId?: string) => {
    if (!game || !eventId) return;
    if (!window.confirm('Are you sure you want to delete this event marker?')) return;
    const deletedGoal = (game.goals || []).find((g) => matchesGoalId(g, eventId));
    setDeletingEventId(eventId);
    try {
      await deleteGameEvent(game.gameId, eventId);
      const updated = await fetchGameVideoDetails(game.gameId);
      setGame(updated);
      if (deletedGoal) {
        setUndoStack((prev) => [
          ...prev,
          {
            type: 'delete_goal',
            description: `Deleted Goal #${deletedGoal.goalNumber}`,
            prev: deletedGoal,
            next: null,
          },
        ]);
        setRedoStack([]);
      }
      setSelectedTimelineItem(null);
    } catch (err: any) {
      alert(err.message || 'Failed to delete event');
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleDeletePeriod = async (periodNumber: number) => {
    if (!game) return;
    const pObj = periodsRef.current.find((p) => p.period === periodNumber);
    const pLabel = pObj?.label || `Period ${periodNumber}`;
    if (!window.confirm(`Are you sure you want to delete ${pLabel}?`)) return;

    const prevPeriods = [...periodsRef.current];
    const updated = periodsRef.current.filter((p) => p.period !== periodNumber);
    setPeriods(updated);
    setGame((prev) => (prev ? { ...prev, periods: updated } : prev));
    setUndoStack((prev) => [
      ...prev,
      {
        type: 'period',
        description: `Deleted ${pLabel}`,
        prev: prevPeriods,
        next: updated,
      },
    ]);
    setRedoStack([]);
    setSelectedTimelineItem(null);

    try {
      const res = await updateGamePeriods(game.gameId, updated);
      const serverPeriods = res?.periods ?? updated;
      setPeriods(serverPeriods);
      setGame((prev) => (prev ? { ...prev, periods: serverPeriods } : prev));
    } catch (err: any) {
      console.error('Failed to delete period:', err);
      alert(err.message || 'Failed to delete period');
    }
  };

  const handleUpdatePeriod = async (periodNumber: number, newStart: number, newEnd: number) => {
    if (!game) return;
    const prevPeriods = [...periodsRef.current];
    const updated = periodsRef.current.map((p) =>
      p.period === periodNumber
        ? { ...p, startTime: newStart, endTime: newEnd, isCustom: true, isLocked: true }
        : p
    );
    setPeriods(updated);
    setGame((prev) => (prev ? { ...prev, periods: updated } : prev));
    setUndoStack((prev) => [
      ...prev,
      {
        type: 'period',
        description: `Updated Period ${periodNumber} (${formatSeconds(newStart)} - ${formatSeconds(newEnd)})`,
        prev: prevPeriods,
        next: updated,
      },
    ]);
    setRedoStack([]);
    setUnlockedTimelineItems((prev) =>
      prev.filter((item) => !(item.type === 'period' && item.id === periodNumber))
    );

    try {
      const res = await updateGamePeriods(game.gameId, updated);
      const serverPeriods = res?.periods && res.periods.length > 0 ? res.periods : updated;
      setPeriods(serverPeriods);
      setGame((prev) => (prev ? { ...prev, periods: serverPeriods } : prev));
    } catch (err: any) {
      console.error('Failed to update period:', err);
      alert(err.message || 'Failed to update period');
    }
  };

  const handleUpdateGoal = async (goalIdOrNumber: number | string, updates: Partial<GoalEventData>) => {
    if (!game) return;
    const existing = game.goals?.find((g) => matchesGoalId(g, goalIdOrNumber));
    if (!existing) return;
    const targetId = getGoalIdentifier(existing);
    const prevGoal = JSON.parse(JSON.stringify(existing));
    const nextGoal: GoalEventData = {
      ...existing,
      ...updates,
      ...(updates.videoTimestamp !== undefined
        ? { videoTimestampStr: formatSeconds(updates.videoTimestamp) }
        : {}),
      isModified: true,
      isLocked: true,
    };

    const resequencedLocal = sequenceGoalsChronologically(
      (game.goals || []).map((g) => (matchesGoalId(g, targetId) ? nextGoal : g))
    );
    const updatedEntryLocal = resequencedLocal.find((g) => matchesGoalId(g, targetId)) || nextGoal;

    setGame((prev) => {
      if (!prev) return null;
      const mapped = (prev.goals || []).map((g) => (matchesGoalId(g, targetId) ? nextGoal : g));
      return {
        ...prev,
        goals: sequenceGoalsChronologically(mapped),
      };
    });

    if (selectedTimelineItem?.type === 'goal') {
      const selMatches =
        (selectedTimelineItem.eventId && matchesGoalId(existing, selectedTimelineItem.eventId)) ||
        selectedTimelineItem.goalNumber === existing.goalNumber;
      if (selMatches) {
        const evtId = updatedEntryLocal.eventId || updatedEntryLocal.id;
        setSelectedTimelineItem({
          type: 'goal',
          goalNumber: updatedEntryLocal.goalNumber,
          ...(evtId ? { eventId: evtId } : {}),
        });
      }
    }

    setUndoStack((prev) => [
      ...prev,
      {
        type: 'goal',
        description: `Updated Goal #${updatedEntryLocal.goalNumber}`,
        prev: prevGoal,
        next: updatedEntryLocal,
      },
    ]);
    setRedoStack([]);
    setUnlockedTimelineItems((prev) =>
      prev.filter(
        (item) =>
          !(item.type === 'goal' && (String(item.id) === String(targetId) || matchesGoalId(existing, item.id)))
      )
    );

    try {
      const res = await updateGameGoal(game.gameId, targetId, { ...updates, isLocked: true });
      const serverGoals = (res as any)?.goals;
      const serverGoal =
        res?.goal && Object.keys(res.goal).length > 0 ? { ...updatedEntryLocal, ...res.goal } : updatedEntryLocal;
      setGame((prev) => {
        if (!prev) return null;
        if (Array.isArray(serverGoals) && serverGoals.length > 0) {
          return {
            ...prev,
            goals: serverGoals,
          };
        }
        const mapped = (prev.goals || []).map((g) => (matchesGoalId(g, targetId) ? serverGoal : g));
        return {
          ...prev,
          goals: sequenceGoalsChronologically(mapped),
        };
      });
    } catch (err: any) {
      console.error('Failed to update goal:', err);
      alert(err.message || 'Failed to update goal');
    }
  };

  const handleUpdateGoalDraft = (
    goalIdOrNumber: number | string,
    clipStartOrTs?: number,
    clipEnd?: number,
    timestamp?: number,
    metadataUpdates?: Partial<GoalEventData>
  ) => {
    setGame((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        goals: (prev.goals || []).map((g) => {
          if (!matchesGoalId(g, goalIdOrNumber)) return g;
          if (clipEnd === undefined && timestamp === undefined && clipStartOrTs !== undefined && !metadataUpdates) {
            const ts = clipStartOrTs;
            const start = g.clipStartSeconds !== undefined ? g.clipStartSeconds : Math.max(0, ts - 15);
            const end = g.clipEndSeconds !== undefined ? g.clipEndSeconds : ts + 5;
            return {
              ...g,
              videoTimestamp: ts,
              videoTimestampStr: formatSeconds(ts),
              clipStartSeconds: start,
              clipEndSeconds: end,
              durationSeconds: Math.round(Math.max(0, end - start) * 10) / 10,
            };
          }
          const nextStart = clipStartOrTs !== undefined ? clipStartOrTs : g.clipStartSeconds;
          const nextEnd = clipEnd !== undefined ? clipEnd : g.clipEndSeconds;
          let nextTs = timestamp !== undefined ? timestamp : g.videoTimestamp;
          if (nextStart !== undefined && nextEnd !== undefined) {
            nextTs = Math.max(nextStart, Math.min(nextEnd, nextTs || nextStart));
          }
          return {
            ...g,
            ...(metadataUpdates || {}),
            ...(nextStart !== undefined ? { clipStartSeconds: nextStart } : {}),
            ...(nextEnd !== undefined ? { clipEndSeconds: nextEnd } : {}),
            ...(nextStart !== undefined && nextEnd !== undefined
              ? { durationSeconds: Math.round(Math.max(0, nextEnd - nextStart) * 10) / 10 }
              : {}),
            ...(nextTs !== undefined
              ? { videoTimestamp: nextTs, videoTimestampStr: formatSeconds(nextTs) }
              : {}),
          };
        }),
      };
    });
  };

  const handleUpdatePeriodDraft = (periodNumber: number, start: number, end: number) => {
    setPeriods((prev) =>
      prev.map((p) => (p.period === periodNumber ? { ...p, startTime: start, endTime: end, isCustom: true } : p))
    );
    setGame((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        periods: (prev.periods || []).map((p) =>
          p.period === periodNumber ? { ...p, startTime: start, endTime: end, isCustom: true } : p
        ),
      };
    });
  };

  const [savingAll, setSavingAll] = useState<boolean>(false);

  const handleSaveAll = async () => {
    if (!game) return;
    setSavingAll(true);
    try {
      const hasUnlockedPeriods = unlockedTimelineItems.some((item) => item.type === 'period');
      if (hasUnlockedPeriods) {
        const lockedPeriods = periods.map((p) => ({ ...p, isLocked: true }));
        setPeriods(lockedPeriods);
        setGame((prev) => (prev ? { ...prev, periods: lockedPeriods } : prev));
        const res = await updateGamePeriods(game.gameId, lockedPeriods);
        const serverPeriods = res?.periods && res.periods.length > 0 ? res.periods : lockedPeriods;
        setPeriods(serverPeriods);
        setGame((prev) => (prev ? { ...prev, periods: serverPeriods } : prev));
      }

      const unlockedGoals = unlockedTimelineItems.filter((item) => item.type === 'goal');
      for (const item of unlockedGoals) {
        const goal = gameRef.current?.goals?.find((g) => matchesGoalId(g, item.id));
        if (goal) {
          const targetId = getGoalIdentifier(goal);
          if (item.initialSnapshot) {
            setUndoStack((prev) => [
              ...prev,
              {
                type: 'goal',
                description: `Updated Goal #${goal.goalNumber}`,
                prev: item.initialSnapshot,
                next: { ...goal, isModified: true, isLocked: true },
              },
            ]);
            setRedoStack([]);
          }
          await updateGameGoal(game.gameId, targetId, { ...goal, isModified: true, isLocked: true });
        }
      }
      if (unlockedGoals.length > 0) {
        setGame((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            goals: sequenceGoalsChronologically(
              (prev.goals || []).map((g) => ({ ...g, isLocked: true }))
            ),
          };
        });
      }

      const unlockedClips = unlockedTimelineItems.filter((item) => item.type === 'clip');
      for (const item of unlockedClips) {
        const clip = timelineEvents.find((ev) => ev.id === item.id && ev.eventType === 'clip') as TimelineClip | undefined;
        if (clip) {
          await updateTimelineEvent(game.gameId, clip.id, { ...clip, isLocked: true });
        }
      }

      const unlockedNotes = unlockedTimelineItems.filter((item) => item.type === 'note');
      for (const item of unlockedNotes) {
        const note = timelineEvents.find((ev) => ev.id === item.id && ev.eventType === 'note') as TimelineNote | undefined;
        if (note) {
          await updateTimelineEvent(game.gameId, note.id, { ...note, isLocked: true });
        }
      }

      setUnlockedTimelineItems([]);
      setDownloadSnackbar({
        text: 'Saved and locked all open changes.',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Failed to save all changes:', err);
      alert(err.message || 'Failed to save all changes');
    } finally {
      setSavingAll(false);
    }
  };

  const jumpToGoal = (timestampSeconds: number) => {
    setIsReelMode(false);
    // Jump to the start of the goal play/highlight clip if available, otherwise a 15-second lead-in
    const matchH = game?.highlights?.find(
      (h) =>
        Math.abs(h.videoTimestamp - timestampSeconds) < 4.0 ||
        (h.clipStartSeconds <= timestampSeconds && h.clipEndSeconds >= timestampSeconds)
    );
    const eventStart =
      matchH?.clipStartSeconds !== undefined ? matchH.clipStartSeconds : Math.max(0, timestampSeconds - 15);

    handleSeek(eventStart);
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const jumpToPeriod = (periodNumber: number) => {
    setIsReelMode(false);
    const matchedP = periodsRef.current.find((p) => p.period === periodNumber);
    if (matchedP) {
      handleSeek(matchedP.startTime);
      if (videoRef.current && videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      }
      return;
    }
    if (!game?.timeline) return;

    const baseStart = game.timeline.puckDrop || game.timeline.actualGameStart;

    if (periodNumber === 1) {
      handleSeek(0);
    } else if (periodNumber === 2 && game.timeline.period2Start && baseStart) {
      const p1 = new Date(`1970-01-01T${baseStart}Z`).getTime();
      const p2 = new Date(`1970-01-01T${game.timeline.period2Start}Z`).getTime();
      const diffSec = Math.max(0, (p2 - p1) / 1000);
      handleSeek(diffSec);
    } else if (periodNumber === 3 && game.timeline.period3Start && baseStart) {
      const p1 = new Date(`1970-01-01T${baseStart}Z`).getTime();
      const p3 = new Date(`1970-01-01T${game.timeline.period3Start}Z`).getTime();
      const diffSec = Math.max(0, (p3 - p1) / 1000);
      handleSeek(diffSec);
    } else if (periodNumber === 4 && game.timeline.overtimeStart && baseStart) {
      const p1 = new Date(`1970-01-01T${baseStart}Z`).getTime();
      const ot = new Date(`1970-01-01T${game.timeline.overtimeStart}Z`).getTime();
      const diffSec = Math.max(0, (ot - p1) / 1000);
      handleSeek(diffSec);
    }
  };

  // Highlights Reel Mode Playback
  const highlights = game?.highlights || [];

  const playHighlightClip = async (index: number) => {
    if (!highlights[index] || !game) return;
    setIsReelMode(true);
    setCurrentReelIndex(index);
    const hl = highlights[index];
    const cleanFilename = hl.filename || (hl.clipUrl ? (hl.clipUrl.includes('/') ? hl.clipUrl.substring(hl.clipUrl.lastIndexOf('/') + 1).split('?')[0] : hl.clipUrl) : 'highlight.mp4');

    try {
      const streamInfo = await fetchHighlightClipStreamUrl(game.gameId, cleanFilename).catch(() => null);
      const curToken = await getAuthToken();
      const clipUrl = streamInfo?.direct
        ? streamInfo.url
        : (streamInfo?.url ? `${getBackendUrl()}${streamInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}` : `${getBackendUrl()}${getHighlightClipUrl(game.gameId, cleanFilename)}${curToken ? `?token=${encodeURIComponent(curToken)}` : ''}`);
      setActiveStreamUrl(clipUrl);
      if (videoRef.current) {
        videoRef.current.src = clipUrl;
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    } catch (e) {
      const curToken = await getAuthToken();
      const fallbackUrl = `${getBackendUrl()}${getHighlightClipUrl(game.gameId, cleanFilename)}${curToken ? `?token=${encodeURIComponent(curToken)}` : ''}`;
      setActiveStreamUrl(fallbackUrl);
      if (videoRef.current) {
        videoRef.current.src = fallbackUrl;
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnded = () => {
    if (isReelMode && currentReelIndex < highlights.length - 1) {
      // Auto-advance to next highlight
      playHighlightClip(currentReelIndex + 1);
    } else {
      setIsPlaying(false);
      if (!isReelMode && game?.gameId) {
        try {
          localStorage.removeItem(`jets_playback_pos_${game.gameId}`);
        } catch {}
        syncUrlTimestamp(0, selectedFeed);
      }
    }
  };

  const handleVideoPlaybackError = async (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.warn('Video playback error on feed', selectedFeed, e);

    if (isRecoveringRef.current || recoveryAttemptsRef.current >= 3) {
      if (selectedFeed === 'pano' && hasAuto) {
        setFeedNotice('Panoramic feed is currently unavailable or still processing. Switched to auto-tracking feed.');
        if (videoRef.current) {
          const oldOffset = game?.feedSync?.offsets?.['pano'] ?? 0;
          const newOffset = game?.feedSync?.offsets?.['auto'] ?? 0;
          const baselineTime = Math.max(0, videoRef.current.currentTime - oldOffset);
          pendingSeekTimeRef.current = Math.max(0, baselineTime + newOffset);
          wasPlayingBeforeFeedChange.current = !videoRef.current.paused;
        }
        setSelectedFeed('auto');
      } else {
        setFeedNotice('Unable to load video stream. Please verify your connection or try refreshing.');
      }
      return;
    }

    isRecoveringRef.current = true;
    recoveryAttemptsRef.current += 1;

    // Capture playback position & state for seamless resumption
    const currentPos = videoRef.current ? videoRef.current.currentTime : currentTime;
    const wasPlaying = videoRef.current ? !videoRef.current.paused : isPlaying;
    pendingSeekTimeRef.current = currentPos;
    wasPlayingBeforeFeedChange.current = wasPlaying;

    try {
      // Force refresh the auth token
      const freshToken = await refreshAuthToken();
      if (freshToken) {
        setIdToken(freshToken);
      }

      // Re-fetch direct GCS signed URL (or stream URL)
      if (game) {
        let freshUrl = '';
        if (isReelMode && highlights[currentReelIndex]) {
          const hl = highlights[currentReelIndex];
          const cleanFilename = hl.filename || (hl.clipUrl ? (hl.clipUrl.includes('/') ? hl.clipUrl.substring(hl.clipUrl.lastIndexOf('/') + 1).split('?')[0] : hl.clipUrl) : 'highlight.mp4');
          const streamInfo = await fetchHighlightClipStreamUrl(game.gameId, cleanFilename).catch(() => null);
          freshUrl = streamInfo?.direct
            ? streamInfo.url
            : `${getBackendUrl()}${getHighlightClipUrl(game.gameId, cleanFilename)}${freshToken ? `?token=${encodeURIComponent(freshToken)}` : ''}`;
        } else {
          const streamInfo = await fetchVideoStreamUrl(game.gameId, selectedFeed).catch(() => null);
          freshUrl = streamInfo?.direct
            ? streamInfo.url
            : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${freshToken ? `&token=${encodeURIComponent(freshToken)}` : ''}`;
        }

        if (freshUrl && videoRef.current) {
          setActiveStreamUrl(freshUrl);
          videoRef.current.src = freshUrl;
          videoRef.current.load();
          setFeedNotice(null);
          setTimeout(() => {
            recoveryAttemptsRef.current = 0;
          }, 10000);
        }

        if (!isReelMode) {
          try {
            const scrubInfo = await fetchVideoStreamUrl(game.gameId, selectedFeed, 'scrub').catch(() => null);
            const freshScrubUrl = scrubInfo?.direct
              ? scrubInfo.url
              : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}&variant=scrub${freshToken ? `&token=${encodeURIComponent(freshToken)}` : ''}`;
            setActiveScrubStreamUrl(freshScrubUrl);
            if (scrubVideoRef.current) {
              scrubVideoRef.current.src = freshScrubUrl;
              scrubVideoRef.current.load();
            }
          } catch {}
        }
      }
    } catch (refreshErr) {
      console.error('Silent stream renewal failed:', refreshErr);
      if (selectedFeed === 'pano' && hasAuto) {
        setSelectedFeed('auto');
      } else {
        setFeedNotice('Unable to load video stream. Please verify your connection or try refreshing.');
      }
    } finally {
      isRecoveringRef.current = false;
    }
  };

  const handleDownloadCustomClip = async () => {
    if (!gameId) return;
    setCuttingClip(true);
    setClipError(null);
    setDownloadSnackbar({
      text: `Extracting custom ${(clipEnd - clipStart).toFixed(1)}s clip... Please wait.`,
      severity: 'info',
    });
    try {
      const curFeedOffset = (game?.feedSync?.offsets?.[selectedFeed]) ?? 0;
      const targetStart = Math.max(0, clipStart + curFeedOffset);
      const targetEnd = Math.max(targetStart + 1, clipEnd + curFeedOffset);
      const blob = await downloadCustomClip(gameId, targetStart, targetEnd, selectedFeed);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chicagojets_${gameId}_${clipStart}s_${clipEnd}s.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setClipperOpen(false);
      setDownloadSnackbar({
        text: 'Custom clip downloaded successfully!',
        severity: 'success',
      });
    } catch (err: any) {
      const msg = err.message || 'Failed to download custom clip.';
      setClipError(msg);
      setDownloadSnackbar({
        text: `Error downloading clip: ${msg}`,
        severity: 'error',
      });
    } finally {
      setCuttingClip(false);
    }
  };

  const handleDownloadRangeClip = async (startSec: number, endSec: number) => {
    if (!gameId) return;
    setDownloadingRangeClip(true);
    setClipError(null);
    const clipLen = Math.max(0.1, endSec - startSec).toFixed(1);
    setDownloadSnackbar({
      text: `Preparing ${clipLen}s clip for download... Please wait.`,
      severity: 'info',
    });
    try {
      const s = Math.max(0, Math.floor(startSec));
      const e = Math.min(Math.ceil(duration || endSec), Math.ceil(endSec));
      const curFeedOffset = (game?.feedSync?.offsets?.[selectedFeed]) ?? 0;
      const targetStart = Math.max(0, s + curFeedOffset);
      const targetEnd = Math.max(targetStart + 1, e + curFeedOffset);
      const blob = await downloadCustomClip(gameId, targetStart, targetEnd, selectedFeed);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chicagojets_${gameId}_${s}s_${e}s.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDownloadSnackbar({
        text: `Clip successfully downloaded! (${(e - s)}s)`,
        severity: 'success',
      });
    } catch (err: any) {
      const msg = err.message || 'Failed to download custom clip.';
      setClipError(msg);
      setDownloadSnackbar({
        text: `Error downloading clip: ${msg}`,
        severity: 'error',
      });
    } finally {
      setDownloadingRangeClip(false);
    }
  };

  const handleDownloadHighlightClip = async (hl: GoalHighlight | { clipUrl: string; filename?: string; title?: string }) => {
    if (!game) return;
    const rawName = hl.filename || (hl.clipUrl ? (hl.clipUrl.includes('/') ? hl.clipUrl.substring(hl.clipUrl.lastIndexOf('/') + 1).split('?')[0] : hl.clipUrl) : 'highlight.mp4');
    const filename = rawName.endsWith('.mp4') ? rawName : `${rawName}.mp4`;

    setDownloadSnackbar({
      text: `Preparing ${filename} for download...`,
      severity: 'info',
    });

    try {
      const blob = await downloadHighlightClip(game.gameId, filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setDownloadSnackbar({
        text: 'Highlight clip successfully downloaded!',
        severity: 'success',
      });
    } catch (err: any) {
      const msg = err.message || 'Failed to download highlight clip.';
      setDownloadSnackbar({
        text: `Error downloading clip: ${msg}`,
        severity: 'error',
      });
    }
  };

  const renderRulerTicks = () => {
    if (!duration || duration <= 0) return null;
    const secPerPx = duration / trackWidth;
    const targetPx = 80;
    const targetSec = targetPx * secPerPx;

    const possibleSteps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
    let step = possibleSteps[possibleSteps.length - 1];
    for (const s of possibleSteps) {
      if (s >= targetSec) {
        step = s;
        break;
      }
    }
    const minorStep = step / (step <= 0.2 ? 2 : 5);

    const ticks = [];
    const maxTicks = 400;
    let count = 0;
    for (let t = 0; t <= duration + 0.001 && count < maxTicks; t += minorStep) {
      count++;
      const isMajor = Math.abs(Math.round(t / step) * step - t) < (minorStep / 2);
      const px = timeToPx(t);
      if (px > trackWidth) break;

      ticks.push(
        <React.Fragment key={`tick-${t.toFixed(2)}`}>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: `${px}px`,
              width: '1px',
              height: isMajor ? 10 : 5,
              bgcolor: isMajor ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
              pointerEvents: 'none',
            }}
          />
          {isMajor && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: 2,
                left: `${px}px`,
                transform: 'translateX(-50%)',
                fontSize: '0.62rem',
                fontFamily: 'monospace',
                color: 'rgba(255,255,255,0.75)',
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {formatSeconds(t)}
            </Typography>
          )}
        </React.Fragment>
      );
    }
    return ticks;
  };


  if (!isAuthReady) {
    return (
      <ThemeProvider theme={studioTheme}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2, bgcolor: '#141416', color: '#fff' }}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary">
            Loading game replay...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <ThemeProvider theme={studioTheme}>
        <Box sx={{ p: 2, bgcolor: '#141416', minHeight: '90vh', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="rounded" width={100} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Skeleton variant="text" width={220} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Skeleton variant="rounded" width={120} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, height: '70vh' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rounded" width="100%" height="100%" sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
              <Skeleton variant="rounded" width="100%" height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: 340, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 1.5 }}>
              <Skeleton variant="rounded" width="100%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              <Skeleton variant="rounded" width="100%" height="100%" sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (error || !game) {
    return (
      <ThemeProvider theme={studioTheme}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3, bgcolor: '#141416', color: '#fff', minHeight: '100vh' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Game video not found.'}
          </Alert>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/highlights')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>
            Back to Highlights
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  const hasAuto = game.feeds?.auto?.status === 'downloaded';
  const hasPano = game.feeds?.pano?.status === 'downloaded';

  const fallbackUrl = isReelMode && highlights[currentReelIndex]
    ? `${getBackendUrl()}${getHighlightClipUrl(game.gameId, highlights[currentReelIndex].clipUrl)}${idToken ? `?token=${encodeURIComponent(idToken)}` : ''}`
    : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${idToken ? `&token=${encodeURIComponent(idToken)}` : ''}`;
  const videoStreamUrl = activeStreamUrl || fallbackUrl;

  const fallbackScrubUrl = `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}&variant=scrub${idToken ? `&token=${encodeURIComponent(idToken)}` : ''}`;
  const scrubStreamUrl = activeScrubStreamUrl || fallbackScrubUrl;

  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
      const rawT = videoRef.current.currentTime;
      const t = Math.max(0, rawT - curFeedOffset);

      if (hasClipMark && !videoRef.current.paused) {
        if (isPreviewingClipRegionRef.current && t >= rangeClipEnd) {
          videoRef.current.pause();
          setIsPlaying(false);
          const targetVideoTime = Math.max(0, rangeClipStart + curFeedOffset);
          videoRef.current.currentTime = targetVideoTime;
          lastDispatchedSeekTimeRef.current = targetVideoTime;
          lastTargetTimelineTimeRef.current = rangeClipStart;
          setCurrentTime(rangeClipStart);
          if (zoom > 1.05) drawMinimapFrame();
          return;
        } else if (t >= rangeClipStart && t < rangeClipEnd) {
          isPreviewingClipRegionRef.current = true;
        }
      }
      lastDispatchedSeekTimeRef.current = rawT;
      lastTargetTimelineTimeRef.current = t;
      setCurrentTime(t);
      if (zoom > 1.05) drawMinimapFrame();

      // Save playback position periodically (every 2.5s) if watching full game
      if (!isReelMode && game?.gameId) {
        const now = Date.now();
        if (now - lastSavedPosRef.current > 2500) {
          lastSavedPosRef.current = now;
          try {
            localStorage.setItem(
              `jets_playback_pos_${game.gameId}`,
              JSON.stringify({
                time: Math.floor(t),
                feed: selectedFeed,
                updatedAt: now,
              })
            );
          } catch {}
        }
        if (now - lastSyncedUrlTimeRef.current > 5000) {
          lastSyncedUrlTimeRef.current = now;
          syncUrlTimestamp(t, selectedFeed);
        }
      }
    }
  };

  const handleVideoSeeked = () => {
    clearDelayedSeekIndicator();
    clearStoryboardOverlay();
    setIsVideoBuffering(false);
    isExecutingSeekRef.current = false;
    setIsScrubbingUI(false);
    if (scrubProxyHideTimeoutRef.current) {
      clearTimeout(scrubProxyHideTimeoutRef.current);
      scrubProxyHideTimeoutRef.current = null;
    }
    if (scrubSafetyTimeoutRef.current) {
      clearTimeout(scrubSafetyTimeoutRef.current);
      scrubSafetyTimeoutRef.current = null;
    }

    const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);

    if (!isActivelyScrubbingRef.current) {
      if (zoom > 1.05) drawMinimapFrame();
      if (videoRef.current && !isReelMode && game?.gameId) {
        const baselineT = Math.max(0, videoRef.current.currentTime - curFeedOffset);
        try {
          localStorage.setItem(
            `jets_playback_pos_${game.gameId}`,
            JSON.stringify({
              time: Math.floor(baselineT),
              feed: selectedFeed,
              updatedAt: Date.now(),
            })
          );
        } catch {}
        syncUrlTimestamp(baselineT, selectedFeed);
      }
    }

    // Process next pending scrub time if user dragged mouse to a new position during seek execution
    if (
      pendingScrubTimeRef.current !== null &&
      lastDispatchedSeekTimeRef.current !== null &&
      Math.abs(Math.max(0, pendingScrubTimeRef.current + curFeedOffset) - lastDispatchedSeekTimeRef.current) >= 0.05
    ) {
      const nextTime = pendingScrubTimeRef.current;
      executeSeek(nextTime, true);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
        setVideoAspectRatio(videoRef.current.videoWidth / videoRef.current.videoHeight);
      }
      if (videoRef.current.clientWidth > 0 && videoRef.current.clientHeight > 0) {
        setVideoElementRect({ width: videoRef.current.clientWidth, height: videoRef.current.clientHeight });
      }
      if (pendingSeekTimeRef.current !== null) {
        const targetT = Math.min(pendingSeekTimeRef.current, videoRef.current.duration || pendingSeekTimeRef.current);
        videoRef.current.currentTime = targetT;
        lastDispatchedSeekTimeRef.current = targetT;
        const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
        const baselineT = Math.max(0, targetT - curFeedOffset);
        lastTargetTimelineTimeRef.current = baselineT;
        setCurrentTime(baselineT);
        pendingSeekTimeRef.current = null;
        if (wasPlayingBeforeFeedChange.current) {
          videoRef.current.play().catch(() => {});
          setIsPlaying(true);
          wasPlayingBeforeFeedChange.current = false;
        }
      }
      drawMinimapFrame();
    }
  };

  if (isMobile) {
    return (
      <ThemeProvider theme={studioTheme}>
        <MobileWatchView
          storyboardMetadata={storyboardMetadata}
          videoAspectRatio={videoAspectRatio}
          authToken={idToken}
          game={game}
          highlights={highlights}
          isJetsHome={isJetsHome}
          videoRef={videoRef}
          videoStreamUrl={videoStreamUrl}
          scrubVideoRef={scrubVideoRef}
          scrubStreamUrl={scrubStreamUrl}
          isScrubbingUI={isScrubbingUI}
          isVideoBuffering={isVideoBuffering}
          isDelayedSeeking={isDelayedSeeking}
          delayedSeekTime={delayedSeekTime}
          selectedFeed={selectedFeed}
          hasAuto={hasAuto}
          hasPano={hasPano}
          onFeedChange={handleFeedChange}
          isPlaying={isPlaying}
          onTogglePlay={togglePlay}
          currentTime={currentTime}
          duration={duration}
          onSeek={handleSeek}
          onRelativeSeek={handleRelativeSeek}
          playbackRate={playbackRate}
          onSetPlaybackRate={handleSetPlaybackRate}
          onJumpToGoal={jumpToGoal}
          onJumpToPeriod={jumpToPeriod}
          isReelMode={isReelMode}
          currentReelIndex={currentReelIndex}
          onPlayHighlightClip={playHighlightClip}
          onExitReelMode={async () => {
            setIsReelMode(false);
            if (!game) return;
            try {
              const streamInfo = await fetchVideoStreamUrl(game.gameId, selectedFeed).catch(() => null);
              const curToken = await getAuthToken();
              const mainUrl = streamInfo?.direct
                ? streamInfo.url
                : (streamInfo?.url
                  ? `${getBackendUrl()}${streamInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`
                  : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
              setActiveStreamUrl(mainUrl);
              if (videoRef.current) {
                videoRef.current.src = mainUrl;
                videoRef.current.load();
              }
            } catch (e) {
              const curToken = await getAuthToken();
              const fallbackUrl = `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`;
              setActiveStreamUrl(fallbackUrl);
              if (videoRef.current) {
                videoRef.current.src = fallbackUrl;
                videoRef.current.load();
              }
            }
          }}
          onShareCurrentTime={handleShareCurrentTime}
          onDownloadHighlightClip={handleDownloadHighlightClip}
          onVideoPlaybackError={handleVideoPlaybackError}
          onVideoEnded={handleVideoEnded}
          onTimeUpdate={handleVideoTimeUpdate}
          onSeeking={handleVideoSeeking}
          onSeeked={handleVideoSeeked}
          onWaiting={() => setIsVideoBuffering(true)}
          onPlaying={() => {
            setIsVideoBuffering(false);
            clearDelayedSeekIndicator();
          }}
          onCanPlay={() => {
            setIsVideoBuffering(false);
            clearDelayedSeekIndicator();
          }}
          onLoadedMetadata={handleVideoLoadedMetadata}
          onToggleDesktopMode={handleToggleDesktopMode}
          timelineEvents={timelineEvents}
          onSelectTimelineEvent={(ev) => {
            if (ev.eventType === 'note') {
              handleSeek(ev.time, false);
              setSelectedTimelineItem({ type: 'note', id: ev.id });
            } else {
              handleSeek(ev.startTime, false);
              setSelectedTimelineItem({ type: 'clip', id: ev.id });
            }
          }}
          onDownloadClipPrompt={(clip) => {
            setDownloadTargetClip(clip);
            setDownloadFeedDialogOpen(true);
          }}
        />

        {logsModalOpen && game && (
          <TaskDebugLogsModal
            isOpen={logsModalOpen}
            onClose={() => setLogsModalOpen(false)}
            gameId={game.gameId}
            opponent={game.opponent}
            date={game.date}
          />
        )}

        {/* Copy Link Notification */}
        <Snackbar
          open={Boolean(copiedClipUrl)}
          autoHideDuration={3000}
          onClose={() => setCopiedClipUrl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopiedClipUrl(null)} severity="success" sx={{ width: '100%', color: '#fff' }}>
            {(() => {
              if (!copiedClipUrl) return 'Link copied to clipboard!';
              try {
                const urlObj = copiedClipUrl.startsWith('http') ? new URL(copiedClipUrl) : new URL(copiedClipUrl, window.location.origin);
                const tVal = urlObj.searchParams.get('t');
                if (tVal) {
                  return `Share link at ${formatSeconds(parseInt(tVal, 10))} copied to clipboard!`;
                }
              } catch {}
              return 'Clip link copied to clipboard!';
            })()}
          </Alert>
        </Snackbar>

        {/* Clip Download Status Notification */}
        <Snackbar
          open={Boolean(downloadSnackbar)}
          autoHideDuration={downloadSnackbar?.severity === 'info' ? 12000 : 6000}
          onClose={() => setDownloadSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {downloadSnackbar ? (
            <Alert
              severity={downloadSnackbar.severity}
              onClose={() => setDownloadSnackbar(null)}
              sx={{ width: '100%', boxShadow: 4, fontWeight: 500, color: '#fff' }}
              icon={downloadSnackbar.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {downloadSnackbar.text}
            </Alert>
          ) : undefined}
        </Snackbar>

        {/* Feed Choice Download Dialog */}
        <DownloadClipFeedDialog
          open={downloadFeedDialogOpen}
          onClose={() => {
            setDownloadFeedDialogOpen(false);
            setDownloadTargetClip(null);
          }}
          clip={downloadTargetClip}
          hasAuto={hasAuto}
          hasPano={hasPano}
          onDownload={handleConfirmDownloadClipFeed}
        />

        {/* Video Worker Status Notification */}
        {workerNotification && (
          <Snackbar
            open={Boolean(workerNotification)}
            autoHideDuration={workerNotification.severity === 'info' ? 10000 : 6000}
            onClose={() => setWorkerNotification(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity={workerNotification.severity}
              onClose={() => setWorkerNotification(null)}
              variant="filled"
              sx={{ width: '100%', boxShadow: 4, fontWeight: 500, color: '#fff' }}
              icon={workerNotification.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {workerNotification.message}
            </Alert>
          </Snackbar>
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={studioTheme}>
      <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#09090b',
        color: '#fff',
        overflow: 'hidden',
        userSelect: activeSplitter ? 'none' : 'auto',
      }}
    >
      {/* Active Worker Status Banner */}
      {isWorkerActive && (
        <Box
          sx={{
            bgcolor: 'rgba(2, 136, 209, 0.95)',
            color: '#fff',
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 100,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2" fontWeight={600}>
              LiveBarn Worker Active:
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Stage: <strong>{game.currentStage || 'download_pano'}</strong>
              {game.lastHeartbeat ? ` • Heartbeat: ${new Date(game.lastHeartbeat).toLocaleTimeString()}` : ''}
              {' '}(Updating live progress...)
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="contained"
            onClick={() => setLogsModalOpen(true)}
            sx={{ color: '#0288d1', bgcolor: '#fff', textTransform: 'none', fontWeight: 600, py: 0.25, px: 1.5, '&:hover': { bgcolor: '#f0f9ff' } }}
          >
            View Live Transcript
          </Button>
        </Box>
      )}

      {isTaskScheduled && !isWorkerActive && (
        <Box
          sx={{
            bgcolor: 'rgba(217, 119, 6, 0.95)',
            color: '#fff',
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 100,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight={600}>
              Task Queued:
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Execution scheduled for {game.scheduledForFormatted || (game.scheduledExecutionTime ? new Date(game.scheduledExecutionTime).toLocaleTimeString() : 'queue')}
              {game.delayReason ? ` (${game.delayReason})` : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canManageEvents && (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleTriggerRetry('pano')}
                disabled={isRetrying}
                startIcon={isRetrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                sx={{ bgcolor: '#fff', color: '#b45309', textTransform: 'none', fontWeight: 600, py: 0.25, px: 1.5, '&:hover': { bgcolor: '#fef3c7' } }}
              >
                {isRetrying ? 'Starting...' : 'Trigger Now'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setLogsModalOpen(true)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', py: 0.25, px: 1.5 }}
            >
              Logs
            </Button>
          </Stack>
        </Box>
      )}

      {game.status === 'partial' && !hasPano && !isTaskScheduled && !isWorkerActive && (
        <Box
          sx={{
            bgcolor: 'rgba(194, 65, 12, 0.95)',
            color: '#fff',
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 100,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight={600}>
              Panoramic Feed Pending:
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              {game.delayReason || 'LiveBarn awaiting panoramic camera feed availability'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canManageEvents && (
              <Button
                size="small"
                variant="contained"
                onClick={() => handleTriggerRetry('pano')}
                disabled={isRetrying}
                startIcon={isRetrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                sx={{ bgcolor: '#fff', color: '#c2410c', textTransform: 'none', fontWeight: 600, py: 0.25, px: 1.5, '&:hover': { bgcolor: '#ffedd5' } }}
              >
                {isRetrying ? 'Starting...' : 'Retry Pano Feed'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setLogsModalOpen(true)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', py: 0.25, px: 1.5 }}
            >
              Logs
            </Button>
          </Stack>
        </Box>
      )}

      {/* ========================================================
          UPPER WORKSPACE: VIDEO CANVAS (LEFT) + INSPECTOR (RIGHT)
          ======================================================== */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Left/Center Pane: Scalable Video Canvas & Pinned Transport Toolbar */}
        <Box
          ref={videoWorkspaceRef}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#000',
            position: 'relative',
            overflow: 'hidden',
            ...(isFullscreen
              ? {
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100vw',
                  height: '100vh',
                  zIndex: 9999,
                }
              : {}),
          }}
        >
          {/* Interactive Video Viewport */}
          <Box
            ref={playerWrapperRef}
            sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              bgcolor: '#000',
              cursor: zoom > 1.05 ? (isPanning ? 'grabbing' : 'grab') : 'default',
              touchAction: zoom > 1.05 ? 'none' : 'auto',
            }}
            onPointerDown={handleVideoPointerDown}
            onPointerMove={handleVideoPointerMove}
            onPointerUp={handleVideoPointerUp}
            onPointerCancel={handleVideoPointerCancel}
            onLostPointerCapture={handleVideoPointerCancel}
            onWheel={handleWheelZoom}
          >
            {/* Floating HUD Back Button (Top-Left) */}
            <Button
              className="player-overlay-control"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/highlights')}
              onPointerDown={(e) => e.stopPropagation()}
              variant="contained"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 20,
                bgcolor: 'rgba(24, 24, 27, 0.75)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'rgba(39, 39, 42, 0.95)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              All Games
            </Button>

            {isMobileBreakpoint && forceDesktopMode && (
              <Button
                className="player-overlay-control"
                onClick={handleToggleDesktopMode}
                onPointerDown={(e) => e.stopPropagation()}
                variant="contained"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 115,
                  zIndex: 20,
                  bgcolor: 'rgba(2, 136, 209, 0.85)',
                  backdropFilter: 'blur(8px)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'rgba(2, 136, 209, 0.95)',
                  },
                }}
              >
                Mobile View
              </Button>
            )}

            {/* Native Video Element with GPU-accelerated Transform */}
            <video
              key={isReelMode ? `reel-${currentReelIndex}` : `${game.gameId}-${selectedFeed}`}
              ref={videoRef}
              src={videoStreamUrl}
              crossOrigin="anonymous"
              muted={isMuted}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: isPanning ? 'none' : 'transform 0.12s ease-out',
                pointerEvents: zoom > 1.05 ? 'none' : 'auto',
                imageRendering: zoom >= 2.5 ? 'pixelated' : 'auto',
              }}
              playsInline
              onTimeUpdate={handleVideoTimeUpdate}
              onSeeking={handleVideoSeeking}
              onSeeked={handleVideoSeeked}
              onWaiting={() => setIsVideoBuffering(true)}
              onPlaying={() => {
                setIsVideoBuffering(false);
                clearDelayedSeekIndicator();
                clearStoryboardOverlay();
              }}
              onCanPlay={() => {
                setIsVideoBuffering(false);
                clearDelayedSeekIndicator();
                clearStoryboardOverlay();
              }}
              onLoadedMetadata={handleVideoLoadedMetadata}
              onError={handleVideoPlaybackError}
              onEnded={handleVideoEnded}
              onClick={togglePlay}
            />

            {/* Ultra-Dense GOP Scrub Proxy Video (360p @ GOP 6 for ~15ms Seeking) */}
            {scrubStreamUrl && !isReelMode && (
              <video
                ref={scrubVideoRef}
                src={scrubStreamUrl}
                crossOrigin="anonymous"
                muted
                playsInline
                style={{
                  position: 'absolute',
                  inset: 0,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'contain',
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  pointerEvents: 'none',
                  opacity: isScrubbingUI ? 1 : 0,
                  visibility: isScrubbingUI ? 'visible' : 'hidden',
                  zIndex: 12,
                }}
                onSeeked={() => {
                  isExecutingSeekRef.current = false;
                  const curFeedOffset = isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0);
                  if (
                    pendingScrubTimeRef.current !== null &&
                    lastDispatchedSeekTimeRef.current !== null &&
                    Math.abs(Math.max(0, pendingScrubTimeRef.current + curFeedOffset) - lastDispatchedSeekTimeRef.current) >= 0.05
                  ) {
                    executeSeek(pendingScrubTimeRef.current, true);
                  }
                }}
              />
            )}

            {/* Scaled-up Blurred Storyboard Overlay in Main Video Area (Fast progressive visual feedback during seek/scrub) */}
            {storyboardMetadata && game?.gameId && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 13,
                  opacity: isStoryboardOverlayActive ? 1 : 0,
                  visibility: isStoryboardOverlayActive ? 'visible' : 'hidden',
                  transition: isStoryboardOverlayActive ? 'none' : 'opacity 0.22s ease-out, visibility 0.22s',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: overlayBoxDimensions.width,
                    height: overlayBoxDimensions.height,
                    aspectRatio: `${effectiveAspectRatio}`,
                    position: 'relative',
                    filter: 'blur(10px)',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center center',
                    transition: isPanning ? 'none' : 'transform 0.12s ease-out',
                  }}
                >
                  <StoryboardPreview
                    gameId={game.gameId}
                    feed={selectedFeed}
                    time={storyboardOverlayTime}
                    metadata={storyboardMetadata}
                    authToken={idToken}
                    width="100%"
                    height="100%"
                    border="none"
                    borderRadius={0}
                    boxShadow="none"
                    showTimecode={false}
                  />
                </Box>
              </Box>
            )}

            {/* Delayed Seek / Loading & Buffering Feedback (Suppressed during scrubbing, shows if seek/load takes >750ms, no blur) */}
            {(isDelayedSeeking || isVideoBuffering) && !isScrubbingUI && !isActivelyScrubbingRef.current && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: 'rgba(0, 0, 0, 0.78)',
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.2,
                  zIndex: 25,
                  pointerEvents: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#38bdf8' }} thickness={4} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                  }}
                >
                  {isDelayedSeeking && delayedSeekTime > 0
                    ? `Loading frame (${formatSeconds(delayedSeekTime)})...`
                    : isDelayedSeeking
                    ? 'Loading frame...'
                    : 'Buffering stream...'}
                </Typography>
              </Box>
            )}

            {/* Floating Video Zoom Controls (Top-Left below Back Button) */}
            <Box
              className="player-overlay-control"
              sx={{
                position: 'absolute',
                top: 56,
                left: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(0,0,0,0.78)',
                backdropFilter: 'blur(6px)',
                borderRadius: 1.5,
                px: 1,
                py: 0.4,
                zIndex: 20,
                border: '1px solid rgba(255,255,255,0.18)',
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Tooltip title="Zoom in video">
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: '#fff', p: 0.5 }}
                    onClick={handleZoomIn}
                    disabled={zoom >= 10.0}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Typography
                variant="caption"
                sx={{
                  color: '#fff',
                  minWidth: 38,
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {Math.round(zoom * 100)}%
              </Typography>

              <Tooltip title="Zoom out video">
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: '#fff', p: 0.5 }}
                    onClick={handleZoomOut}
                    disabled={zoom <= 1.0}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>



              {zoom > 1.05 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleZoomReset}
                  sx={{
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.35)',
                    fontSize: '0.7rem',
                    py: 0.1,
                    px: 0.8,
                    ml: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Reset to 100% Fit
                </Button>
              )}
            </Box>

            {/* Floating Minimap Viewport Navigator (Top-Right) */}
            {zoom > 1.05 && (
              <Box
                ref={minimapBoxRef}
                className="player-overlay-control"
                onPointerDown={handleMinimapPointerDown}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  width: minimapDimensions.width,
                  height: minimapDimensions.height,
                  bgcolor: 'rgba(0,0,0,0.85)',
                  border: '1.5px solid rgba(255,255,255,0.4)',
                  borderRadius: 1,
                  overflow: 'hidden',
                  zIndex: 25,
                  cursor: isMinimapDragging ? 'grabbing' : 'crosshair',
                  touchAction: 'none',
                }}
              >
                <canvas
                  ref={minimapCanvasRef}
                  width={minimapDimensions.width}
                  height={minimapDimensions.height}
                  style={{ width: '100%', height: '100%', display: 'block' }}
                />
                {(() => {
                  const vp = getMinimapViewport();
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        border: '1.5px solid #ef4444',
                        bgcolor: 'rgba(239,68,68,0.22)',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        width: `${vp.width}px`,
                        height: `${vp.height}px`,
                        left: `${vp.left}px`,
                        top: `${vp.top}px`,
                      }}
                    />
                  );
                })()}
              </Box>
            )}

            {/* In-Player Top Right Overlay: Feed Toggle & Debug Logs & Clipper & Sidebar Toggle */}
            <Box
              className="player-overlay-control"
              sx={{
                position: 'absolute',
                top: 12,
                right: zoom > 1.05 ? 144 : 12,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                zIndex: 20,
                transition: 'right 0.2s ease',
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {/* Feed Switcher */}
              {!isReelMode && (hasAuto || hasPano) && (
                <ToggleButtonGroup
                  value={selectedFeed}
                  exclusive
                  size="small"
                  onChange={(_, val) => val && handleFeedChange(val)}
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.78)',
                    backdropFilter: 'blur(6px)',
                    borderRadius: 1,
                    border: '1px solid rgba(255,255,255,0.2)',
                    '& .MuiToggleButton-root': {
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
                    },
                  }}
                >
                  <Tooltip title={hasAuto ? "Auto-tracking Broadcast Feed (1080p)" : "Auto-tracking feed not available"}>
                    <span>
                      <ToggleButton value="auto" disabled={!hasAuto} onClick={() => handleFeedChange('auto')}>
                        <AutoModeIcon sx={{ fontSize: 16, mr: 0.5 }} /> Auto
                      </ToggleButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      hasPano
                        ? "Panoramic Full-Ice Tactical View (4K)"
                        : isWorkerActive
                        ? "Panoramic feed is currently being downloaded and stitched..."
                        : isTaskScheduled
                        ? `Panoramic download queued for ${game.scheduledForFormatted || (game.scheduledExecutionTime ? new Date(game.scheduledExecutionTime).toLocaleTimeString() : 'queue')}`
                        : game.delayReason
                        ? `Panoramic feed delayed: ${game.delayReason}`
                        : "Panoramic feed not yet available"
                    }
                  >
                    <span>
                      <ToggleButton value="pano" disabled={!hasPano} onClick={() => handleFeedChange('pano')}>
                        <PanoramaIcon sx={{ fontSize: 16, mr: 0.5 }} /> Pano
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </ToggleButtonGroup>
              )}

              {/* Admin/Manager Manual Retry Button (when pano is missing) */}
              {canManageEvents && !hasPano && (
                <Tooltip title={isRetrying ? "Retry in progress..." : "Retry Panoramic Feed Download"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleTriggerRetry('pano')}
                      disabled={isRetrying}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.78)',
                        backdropFilter: 'blur(6px)',
                        color: isRetrying ? '#94a3b8' : '#f59e0b',
                        border: '1px solid rgba(245, 158, 11, 0.4)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)', borderColor: '#f59e0b' },
                      }}
                    >
                      {isRetrying ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Debug Logs Button */}
              {canManageEvents && (
                <Tooltip title="View Worker Debug Logs">
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.78)',
                      backdropFilter: 'blur(6px)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                    }}
                    onClick={() => setLogsModalOpen(true)}
                  >
                    <TerminalIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}


              {/* Share Link at Current Time Button */}
              <Tooltip title={`Share link at current time (${formatSeconds(videoRef.current ? Math.max(0, videoRef.current.currentTime - (isReelMode ? 0 : ((game?.feedSync?.offsets?.[selectedFeed]) ?? 0))) : currentTime)})`}>
                <IconButton
                  size="small"
                  aria-label="Share link at current time"
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.78)',
                    backdropFilter: 'blur(6px)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                  }}
                  onClick={handleShareCurrentTime}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Fullscreen Toggle Button */}
              <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc or F)' : 'Fullscreen (F)'}>
                <IconButton
                  size="small"
                  aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.78)',
                    backdropFilter: 'blur(6px)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                  }}
                  onClick={handleToggleFullscreen}
                >
                  {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              {/* Expand Sidebar Button (shown when collapsed) */}
              {isSidebarCollapsed && (
                <Tooltip title="Open Inspector Sidebar">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ChevronLeftIcon />}
                    onClick={toggleSidebarCollapse}
                    sx={{
                      bgcolor: 'rgba(24, 24, 27, 0.85)',
                      backdropFilter: 'blur(6px)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 1,
                      px: 1.25,
                      py: 0.4,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(39, 39, 42, 0.95)' },
                    }}
                  >
                    Inspector
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Feed Notice Alert */}
          {feedNotice && (
            <Alert
              severity="warning"
              onClose={() => setFeedNotice(null)}
              sx={{
                py: 0.25,
                px: 1.5,
                borderRadius: 0,
                fontSize: '0.8rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              {feedNotice}
            </Alert>
          )}

          {/* Pinned Bottom Transport Controls Toolbar */}
          <TransportControlsBar
            isPlaying={isPlaying}
            onTogglePlay={togglePlay}
            onRelativeSeek={handleRelativeSeek}
            onStepFrame={stepFrame}
            playbackRate={playbackRate}
            onSetPlaybackRate={handleSetPlaybackRate}
            onJumpToPeriod={jumpToPeriod}
            hasOvertime={Boolean(game.timeline?.overtimeStart)}
            isReelMode={isReelMode}
            currentReelIndex={currentReelIndex}
            totalReelCount={highlights.length}
            onPlayHighlightClip={playHighlightClip}
            currentTime={currentTime}
            duration={duration > 0 ? duration : (game?.timeline?.durationMinutes ? game.timeline.durationMinutes * 60 : 0)}
            onSeek={(t) => handleSeek(t, false)}
            isMuted={isMuted}
            onToggleMute={() => setIsMuted((prev) => !prev)}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            onExitReelMode={async () => {
              setIsReelMode(false);
              if (!game) return;
              try {
                const streamInfo = await fetchVideoStreamUrl(game.gameId, selectedFeed).catch(() => null);
                const curToken = await getAuthToken();
                const mainUrl = streamInfo?.direct
                  ? streamInfo.url
                  : (streamInfo?.url ? `${getBackendUrl()}${streamInfo.url}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}` : `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`);
                setActiveStreamUrl(mainUrl);
                if (videoRef.current) {
                  videoRef.current.src = mainUrl;
                  videoRef.current.load();
                }
              } catch (e) {
                const curToken = await getAuthToken();
                const fallbackUrl = `${getBackendUrl()}/api/videos/${game.gameId}/stream?feed=${selectedFeed}${curToken ? `&token=${encodeURIComponent(curToken)}` : ''}`;
                setActiveStreamUrl(fallbackUrl);
                if (videoRef.current) {
                  videoRef.current.src = fallbackUrl;
                  videoRef.current.load();
                }
              }
            }}
          />
        </Box>

        {/* Vertical Splitter between Video Canvas and Inspector Sidebar */}
        <Box
          onPointerDown={(e) => {
            e.preventDefault();
            setActiveSplitter('vertical');
          }}
          sx={{
            width: 6,
            flexShrink: 0,
            cursor: 'col-resize',
            bgcolor: activeSplitter === 'vertical' ? 'primary.main' : 'rgba(255, 255, 255, 0.08)',
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            position: 'relative',
            display: isSidebarCollapsed ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              width: 2,
              height: 36,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
            }}
          />
        </Box>

        {/* Right Pane: YouTube-Style Inspector Sidebar */}
        <InspectorSidebar
          game={game}
          highlights={highlights}
          isJetsHome={isJetsHome}
          sidebarWidth={sidebarWidth}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
          canManageEvents={canManageEvents}
          onJumpToGoal={jumpToGoal}
          onJumpToPeriod={jumpToPeriod}
          onPlayHighlightClip={playHighlightClip}
          onDownloadHighlightClip={handleDownloadHighlightClip}
          onOpenAddEvent={handleOpenAddEvent}
          onDeleteEvent={handleDeleteEvent}
          deletingEventId={deletingEventId}
          onOpenLogsModal={() => setLogsModalOpen(true)}
          onCopyShareLink={(url) => setCopiedClipUrl(url)}
          periods={periods}
          selectedTimelineItem={selectedTimelineItem}
          unlockedTimelineItem={unlockedTimelineItem}
          unlockedTimelineItems={unlockedTimelineItems}
          onSaveAll={handleSaveAll}
          onDiscardAll={handleDiscardDraft}
          onUpdatePeriodDraft={handleUpdatePeriodDraft}
          onUpdateGoalDraft={handleUpdateGoalDraft}
          savingAll={savingAll}
          onUnlockItem={handleUnlockItem}
          onLockItem={handleLockItem}
          onDiscardChanges={handleDiscardDraft}
          onDeselectItem={handleDeselectItem}
          onUpdatePeriod={handleUpdatePeriod}
          onDeletePeriod={handleDeletePeriod}
          onUpdateGoal={handleUpdateGoal}
          currentTime={currentTime}
          editHistory={game?.editHistory || []}
          timelineEvents={timelineEvents}
          onUpdateNote={handleUpdateNote}
          onUpdateClip={handleUpdateClip}
          onDeleteTimelineEvent={handleDeleteTimelineEvent}
          onOpenShareModal={(event) => {
            setShareTargetEvent(event);
            setShareModalOpen(true);
          }}
          onDownloadClipPrompt={(clip) => {
            setDownloadTargetClip(clip);
            setDownloadFeedDialogOpen(true);
          }}
          onJumpToTime={(t) => handleSeek(t, false)}
          onSelectItem={(item) => setSelectedTimelineItem(item)}
          onConvertEventType={handleConvertEventType}
        />
      </Box>

      {/* ========================================================
          HORIZONTAL SPLITTER BETWEEN WORKSPACE AND TIMELINE
          ======================================================== */}
      <Box
        onPointerDown={(e) => {
          e.preventDefault();
          setActiveSplitter('horizontal');
        }}
        sx={{
          height: 6,
          width: '100%',
          cursor: 'row-resize',
          bgcolor: activeSplitter === 'horizontal' ? 'primary.main' : 'rgba(255, 255, 255, 0.08)',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'primary.light',
          },
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            height: 2,
            width: 48,
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.3)',
          }}
        />
      </Box>

      {/* ========================================================
          LOWER WORKSPACE: FULL-WIDTH iMOVIE TIMELINE & SCRUBBER
          ======================================================== */}
      <FullWidthTimeline
        gameId={game?.gameId}
        selectedFeed={selectedFeed}
        feedOffset={(game?.feedSync?.offsets?.[selectedFeed]) ?? 0}
        storyboardMetadata={storyboardMetadata}
        videoAspectRatio={videoAspectRatio}
        authToken={idToken}
        timelineHeight={timelineHeight}
        currentTime={currentTime}
        duration={duration}
        hasClipMark={hasClipMark}
        rangeClipStart={rangeClipStart}
        rangeClipEnd={rangeClipEnd}
        rangeClipDuration={rangeClipDuration}
        clipIn={clipIn}
        clipOut={clipOut}
        onSetIn={handleSetIn}
        onSetOut={handleSetOut}
        isPlaying={isPlaying}
        onTogglePlay={togglePlay}
        isMuted={isMuted}
        onToggleMute={() => setIsMuted((prev) => !prev)}
        onDownloadRangeClip={handleDownloadRangeClip}
        downloadingRangeClip={downloadingRangeClip}
        onToggleClipMark={handleToggleClipMark}
        onRecenterClip={() => setCenteredClipRegion(currentTime)}
        onClearClip={handleClearClip}
        timelineZoom={timelineZoom}
        onTimelineZoom={handleTimelineZoom}
        autoScrollTimeline={autoScrollTimeline}
        onToggleAutoScroll={() => setAutoScrollTimeline(!autoScrollTimeline)}
        timelineScrollRef={timelineScrollRef}
        trackWidth={trackWidth}
        timeToPx={timeToPx}
        onTimelineWheel={handleTimelineWheel}
        getTimeFromClientX={getTimeFromClientX}
        handleSeek={handleSeek}
        renderRulerTicks={renderRulerTicks}
        activeTimelineDrag={activeTimelineDrag}
        setActiveTimelineDrag={setActiveTimelineDrag}
        dragStartX={dragStartX}
        dragInitialIn={dragInitialIn}
        dragInitialOut={dragInitialOut}
        onContainerWidthChange={setTimelineBaseWidth}

        // Multi-Track Studio Enhancements
        isJetsHome={isJetsHome}
        targetTeam={game?.targetTeam}
        periods={periods}
        activePeriodNum={
          (activeTimelineDrag === 'period-start' || activeTimelineDrag === 'period-end')
            ? dragPeriodNum.current
            : (unlockedTimelineItem?.type === 'period' ? Number(unlockedTimelineItem.id) : null)
        }
        goals={game?.goals || []}
        highlights={highlights}
        waveform={waveform}
        isLoadingWaveform={isLoadingWaveform}
        selectedTimelineItem={selectedTimelineItem}
        unlockedTimelineItem={unlockedTimelineItem}
        unlockedTimelineItems={unlockedTimelineItems}
        onSaveAll={handleSaveAll}
        savingAll={savingAll}
        onSelectPeriod={handleSelectPeriod}
        onSelectGoal={handleSelectGoal}
        canEditTimeline={canEditTimeline}
        onStartDragPeriod={handleStartDragPeriod}
        onStartDragGoal={handleStartDragGoal}
        onOpenAddGoal={() => handleOpenAddEvent('goal')}
        onOpenAddEvent={() => handleOpenAddEvent('goal')}
        canUndo={undoStack.length > 0}
        canRedo={redoStack.length > 0}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onJumpToPeriod={jumpToPeriod}
        onJumpToGoal={jumpToGoal}

        // Notes & Clips Multi-Row Support
        timelineEvents={timelineEvents}
        onSelectNote={handleSelectNote}
        onSelectClip={handleSelectClip}
        onCreateNote={handleCreateNote}
        onCreateClip={handleCreateClip}
        onStartDragClip={handleStartDragClip}
        onStartDragNote={handleStartDragNote}
      />

      {/* Custom Segment Clipper Dialog */}
      <Dialog
        open={clipperOpen}
        onClose={() => setClipperOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1c1c1f',
            color: '#ffffff',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
          <ContentCutIcon color="primary" />
          Create Custom Video Clip
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)', mb: 2 }}>
            Losslessly extract a custom segment from the game broadcast. Use In/Out hotkeys (<strong>I</strong> and <strong>O</strong>) while playing the video to mark timestamps.
          </Typography>

          {clipError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {clipError}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Start Time (Seconds)"
                type="number"
                fullWidth
                size="small"
                value={clipStart}
                onChange={(e) => setClipStart(Math.max(0, parseFloat(e.target.value) || 0))}
                helperText={`~${formatSeconds(clipStart)}`}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="End Time (Seconds)"
                type="number"
                fullWidth
                size="small"
                value={clipEnd}
                onChange={(e) => setClipEnd(Math.max(clipStart + 1, parseFloat(e.target.value) || 0))}
                helperText={`~${formatSeconds(clipEnd)}`}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2.5, p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#ffffff' }}>
              Clip Duration: <strong>{(clipEnd - clipStart).toFixed(1)} seconds</strong>
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
              Feed Mode: <strong>{selectedFeed.toUpperCase()}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setClipperOpen(false)} disabled={cuttingClip} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={cuttingClip ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadCustomClip}
            disabled={cuttingClip || clipEnd <= clipStart}
          >
            {cuttingClip ? 'Generating Clip...' : 'Download MP4'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Event Marker Dialog */}
      <Dialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1c1c1f',
            color: '#ffffff',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
          <BookmarkAddIcon color="primary" />
          {eventFormType === 'period' ? 'Add Period / Overtime' : 'Add Game Event Marker'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)', mb: 2 }}>
            {eventFormType === 'period'
              ? 'Define a period boundary (e.g. Overtime) with start and end times.'
              : 'Record a key play, goal, or highlight at the current video timestamp.'}
          </Typography>

          {eventError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {eventError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="event-type-label">Event Type</InputLabel>
              <Select
                labelId="event-type-label"
                value={eventFormType}
                label="Event Type"
                onChange={(e) => {
                  const nextType = e.target.value;
                  setEventFormType(nextType);
                  if (nextType === 'period') {
                    const currentP = Number(eventFormPeriod) || 4;
                    if (!eventFormPeriodLabel) {
                      setEventFormPeriodLabel(currentP === 4 ? 'Overtime' : currentP > 4 ? `Overtime ${currentP - 3}` : `Period ${currentP}`);
                    }
                    if (!eventFormPeriodStart) {
                      setEventFormPeriodStart(formatSeconds(eventFormTimestamp));
                      setEventFormPeriodEnd(formatSeconds(eventFormTimestamp + (currentP >= 4 ? 300 : 900)));
                    }
                  }
                }}
              >
                <MenuItem value="goal">Goal</MenuItem>
                <MenuItem value="period">Period / Overtime</MenuItem>
                <MenuItem value="penalty">Penalty</MenuItem>
                <MenuItem value="save">Big Save</MenuItem>
                <MenuItem value="custom">Custom Highlight</MenuItem>
              </Select>
            </FormControl>

            {eventFormType === 'period' ? (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel id="period-select-label">Period</InputLabel>
                  <Select
                    labelId="period-select-label"
                    value={eventFormPeriod}
                    label="Period"
                    onChange={(e) => {
                      const p = Number(e.target.value);
                      setEventFormPeriod(p);
                      if (p === 1) setEventFormPeriodLabel('Period 1');
                      else if (p === 2) setEventFormPeriodLabel('Period 2');
                      else if (p === 3) setEventFormPeriodLabel('Period 3');
                      else if (p === 4) setEventFormPeriodLabel('Overtime');
                      else if (p === 5) setEventFormPeriodLabel('Overtime 2');
                      else setEventFormPeriodLabel(`Period ${p}`);
                      const defaultDur = p >= 4 ? 300 : 900;
                      const s = parseTimeInput(eventFormPeriodStart) || eventFormTimestamp;
                      setEventFormPeriodEnd(formatSeconds(s + defaultDur));
                    }}
                  >
                    <MenuItem value={1}>Period 1</MenuItem>
                    <MenuItem value={2}>Period 2</MenuItem>
                    <MenuItem value={3}>Period 3</MenuItem>
                    <MenuItem value={4}>Overtime (OT)</MenuItem>
                    <MenuItem value={5}>Overtime 2 (2OT)</MenuItem>
                    <MenuItem value={6}>Custom Period (P6)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Period Label"
                  size="small"
                  fullWidth
                  value={eventFormPeriodLabel}
                  onChange={(e) => setEventFormPeriodLabel(e.target.value)}
                  placeholder="e.g. Overtime or OT"
                  helperText="Display name on timeline and chapters"
                />

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Start Time (MM:SS)"
                      size="small"
                      fullWidth
                      value={eventFormPeriodStart}
                      onChange={(e) => setEventFormPeriodStart(e.target.value)}
                      helperText={`~${formatSeconds(parseTimeInput(eventFormPeriodStart))}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="End Time (MM:SS)"
                      size="small"
                      fullWidth
                      value={eventFormPeriodEnd}
                      onChange={(e) => setEventFormPeriodEnd(e.target.value)}
                      helperText={`~${formatSeconds(parseTimeInput(eventFormPeriodEnd))}`}
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setEventFormPeriodStart(formatSeconds(eventFormTimestamp))}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    Start at Playhead
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setEventFormPeriodEnd(formatSeconds(eventFormTimestamp))}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    End at Playhead
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      const s = parseTimeInput(eventFormPeriodStart);
                      setEventFormPeriodEnd(formatSeconds(s + 300));
                    }}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    +5m OT
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel id="event-team-label">Team</InputLabel>
                  <Select
                    labelId="event-team-label"
                    value={eventFormTeam}
                    label="Team"
                    onChange={(e) => setEventFormTeam(e.target.value)}
                  >
                    <MenuItem value={focalTeamShort}>{focalTeamFull}</MenuItem>
                    <MenuItem value={game.opponent || 'Opponent'}>{game.opponent || 'Opponent'}</MenuItem>
                  </Select>
                </FormControl>

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Period"
                      type="number"
                      size="small"
                      fullWidth
                      value={eventFormPeriod}
                      onChange={(e) => setEventFormPeriod(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      slotProps={{ htmlInput: { min: 1, max: 5 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Score After (e.g. 3-2)"
                      size="small"
                      fullWidth
                      value={eventFormScore}
                      onChange={(e) => setEventFormScore(e.target.value)}
                      placeholder="3-2"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Game Clock (e.g. 12:45)"
                      size="small"
                      fullWidth
                      value={eventFormClock}
                      onChange={(e) => setEventFormClock(e.target.value)}
                      placeholder="12:45"
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      label="Video Time (Seconds)"
                      type="number"
                      size="small"
                      fullWidth
                      value={eventFormTimestamp}
                      onChange={(e) => setEventFormTimestamp(Math.max(0, parseFloat(e.target.value) || 0))}
                      helperText={`~${formatSeconds(eventFormTimestamp)}`}
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Description / Player Note"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={eventFormDescription}
                  onChange={(e) => setEventFormDescription(e.target.value)}
                  placeholder="e.g. Wrist shot top shelf by #22"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEventDialogOpen(false)} disabled={savingEvent} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEvent}
            disabled={savingEvent}
            startIcon={savingEvent ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {savingEvent ? 'Saving...' : eventFormType === 'period' ? 'Save Period' : 'Save Marker'}
          </Button>
        </DialogActions>
      </Dialog>

      {logsModalOpen && game && (
        <TaskDebugLogsModal
          isOpen={logsModalOpen}
          onClose={() => setLogsModalOpen(false)}
          gameId={game.gameId}
          opponent={game.opponent}
          date={game.date}
        />
      )}

      {/* Copy Link Notification */}
      <Snackbar
        open={Boolean(copiedClipUrl)}
        autoHideDuration={3000}
        onClose={() => setCopiedClipUrl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopiedClipUrl(null)} severity="success" sx={{ width: '100%', color: '#fff' }}>
          {(() => {
            if (!copiedClipUrl) return 'Link copied to clipboard!';
            try {
              const urlObj = copiedClipUrl.startsWith('http') ? new URL(copiedClipUrl) : new URL(copiedClipUrl, window.location.origin);
              const tVal = urlObj.searchParams.get('t');
              if (tVal) {
                return `Share link at ${formatSeconds(parseInt(tVal, 10))} copied to clipboard!`;
              }
            } catch {}
            return 'Clip link copied to clipboard!';
          })()}
        </Alert>
      </Snackbar>

      {/* Clip Download Status Notification */}
      <Snackbar
        open={Boolean(downloadSnackbar)}
        autoHideDuration={downloadSnackbar?.severity === 'info' ? 12000 : 6000}
        onClose={() => setDownloadSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {downloadSnackbar ? (
          <Alert
            severity={downloadSnackbar.severity}
            onClose={() => setDownloadSnackbar(null)}
            sx={{ width: '100%', boxShadow: 4, fontWeight: 500, color: '#fff' }}
            icon={downloadSnackbar.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {downloadSnackbar.text}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Video Worker Status Notification */}
      {workerNotification && (
        <Snackbar
          open={Boolean(workerNotification)}
          autoHideDuration={workerNotification.severity === 'info' ? 10000 : 6000}
          onClose={() => setWorkerNotification(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={workerNotification.severity}
            onClose={() => setWorkerNotification(null)}
            variant="filled"
            sx={{ width: '100%', boxShadow: 4, fontWeight: 500, color: '#fff' }}
            icon={workerNotification.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {workerNotification.message}
          </Alert>
        </Snackbar>
      )}

      {/* Timeline Event Share Modal */}
      <TimelineShareModal
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareTargetEvent(null);
        }}
        event={shareTargetEvent}
        rosters={rosters}
        onSaveShares={handleUpdateEventShares}
        onCopyLink={(url) => setCopiedClipUrl(url)}
      />

      {/* Feed Choice Download Dialog */}
      <DownloadClipFeedDialog
        open={downloadFeedDialogOpen}
        onClose={() => {
          setDownloadFeedDialogOpen(false);
          setDownloadTargetClip(null);
        }}
        clip={downloadTargetClip}
        hasAuto={hasAuto}
        hasPano={hasPano}
        onDownload={handleConfirmDownloadClipFeed}
      />
    </Box>
    </ThemeProvider>
  );
};

export default GameWatchPage;
